import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ChildrensBookCoverPlanning from "./ChildrensBookCoverPlanning";
import ChildrensBookPageSpreadPlanning from "./ChildrensBookPageSpreadPlanning";
import ChildrensBookBlueprintOverview from "./ChildrensBookBlueprintOverview";
import ChildrensBookManuscriptPlan from "./ChildrensBookManuscriptPlan";
import ChildrensBookNovelParityV1E from "./ChildrensBookNovelParityV1E";
import ChildrensBookIllustrationPlan from "./ChildrensBookIllustrationPlan";
import ChildrensBookBookAssembly from "./ChildrensBookBookAssembly";
import ChildrensBookBookPreview from "./ChildrensBookBookPreview";
import ChildrensBookPublicationReadiness from "./ChildrensBookPublicationReadiness";
import ChildrensBookPublicationProof from "./ChildrensBookPublicationProof";
import ChildrensBookPublicationHandoff from "./ChildrensBookPublicationHandoff";
import ChildrensBookApprovedEditionGateV1 from "./ChildrensBookApprovedEditionGateV1";
import ChildrensBookWorkspaceNavigator from "./ChildrensBookWorkspaceNavigator";
import ChildrensBookNovelIntegratedWorkspaceV1E4D from "./ChildrensBookNovelIntegratedWorkspaceV1E4D";
import { childrensBookSavedAudienceReady } from "../utils/childrensBookAudienceFirstV1E4X";
import {
  prepareChildrensBookManuscriptImport,
} from "../utils/childrensBookManuscriptImportApplication";

import {
  buildChildrensBookManuscriptUnitDivisionProposal,
} from "../utils/childrensBookManuscriptUnitDivision";

import {
  replaceProjectWorkspacePath,
} from "../utils/projectWorkspaceRouting";

const CHILDRENS_BOOK_BASE_PATH =
  "/my-projects/childrens-book";

const WHOLE_BOOK_REVIEW_ORIGIN_QUERY_KEY =
  "reviewOrigin";

const WHOLE_BOOK_REVIEW_UNIT_QUERY_KEY =
  "reviewUnitId";

const WHOLE_BOOK_REVIEW_WINDOW_QUERY_KEY =
  "reviewWindowStart";

const WHOLE_BOOK_REVIEW_ORIGIN =
  "book-assembly";

function browserChildrenBookSearch() {
  if (typeof window === "undefined") {
    return "";
  }

  return String(
    window.location.search || ""
  );
}

function normalizedWholeBookReviewWindowStart(
  value
) {
  const number = Number(value);

  return (
    Number.isInteger(number) &&
    number >= 0
  )
    ? number
    : 0;
}

export function wholeBookReviewContextFromSearch(
  search = browserChildrenBookSearch()
) {
  const params =
    new URLSearchParams(
      String(search || "")
    );

  if (
    params.get(
      WHOLE_BOOK_REVIEW_ORIGIN_QUERY_KEY
    ) !== WHOLE_BOOK_REVIEW_ORIGIN
  ) {
    return null;
  }

  return {
    origin:
      WHOLE_BOOK_REVIEW_ORIGIN,

    unitId:
      String(
        params.get(
          WHOLE_BOOK_REVIEW_UNIT_QUERY_KEY
        ) || ""
      ).trim(),

    windowStart:
      normalizedWholeBookReviewWindowStart(
        params.get(
          WHOLE_BOOK_REVIEW_WINDOW_QUERY_KEY
        )
      ),
  };
}

export function wholeBookReviewSearch(
  context,
  search = browserChildrenBookSearch()
) {
  const params =
    new URLSearchParams(
      String(search || "")
    );

  if (
    context?.origin ===
      WHOLE_BOOK_REVIEW_ORIGIN
  ) {
    params.set(
      WHOLE_BOOK_REVIEW_ORIGIN_QUERY_KEY,
      WHOLE_BOOK_REVIEW_ORIGIN
    );

    const unitId =
      String(
        context.unitId || ""
      ).trim();

    if (unitId) {
      params.set(
        WHOLE_BOOK_REVIEW_UNIT_QUERY_KEY,
        unitId
      );
    } else {
      params.delete(
        WHOLE_BOOK_REVIEW_UNIT_QUERY_KEY
      );
    }

    params.set(
      WHOLE_BOOK_REVIEW_WINDOW_QUERY_KEY,
      String(
        normalizedWholeBookReviewWindowStart(
          context.windowStart
        )
      )
    );
  } else {
    params.delete(
      WHOLE_BOOK_REVIEW_ORIGIN_QUERY_KEY
    );

    params.delete(
      WHOLE_BOOK_REVIEW_UNIT_QUERY_KEY
    );

    params.delete(
      WHOLE_BOOK_REVIEW_WINDOW_QUERY_KEY
    );
  }

  const query =
    params.toString();

  return query
    ? `?${query}`
    : "";
}

function initialChildrenBookWorkspace() {
  if (typeof window === "undefined") {
    return "project-plan";
  }

  const path = String(
    window.location.pathname || ""
  ).toLowerCase();

  if (path.endsWith("/book-map")) {
    return "book-map";
  }

  if (path.endsWith("/cover-plan")) {
    return "cover-plan";
  }

  if (path.endsWith("/book-setup")) {
    return "project-plan";
  }

  if (path.endsWith("/manuscript-plan")) {
    return "manuscript-plan";
  }

  if (path.endsWith("/illustration-plan")) {
    return "illustration-plan";
  }

  if (path.endsWith("/book-assembly")) {
    return "book-assembly";
  }

  if (path.endsWith("/book-preview")) {
    return "book-preview";
  }

  if (
    path.endsWith(
      "/publication-readiness"
    )
  ) {
    return "publication-readiness";
  }

  if (
    path.endsWith(
      "/publication-proof"
    )
  ) {
    return "publication-proof";
  }

  if (
    path.endsWith(
      "/publication-handoff"
    )
  ) {
    return "publication-handoff";
  }

  if (
    path.endsWith(
      "/published-book"
    )
  ) {
    return "published-book";
  }

  if (path.endsWith("/blueprint")) {
    return "blueprint";
  }

  if (
    path.endsWith(
      "/project-plan-review"
    )
  ) {
    return "project-plan-review";
  }

  return "project-plan";
}

function replaceChildrenBookWorkspacePath(
  workspace,
  projectId = "",
  wholeBookReviewContext = null
) {
  const identity =
    String(projectId || "").trim();

  return replaceProjectWorkspacePath(
    CHILDRENS_BOOK_BASE_PATH,
    workspace,
    {
      search:
        wholeBookReviewSearch(
          wholeBookReviewContext
        ),

      projectId:
        identity || undefined,
    }
  );
}

export const EMPTY_DRAFT = {
  projectType: "children_book",
  studioType: "children_book_studio",

  identity: {
    title: "",
    subtitle: "",
    language: "en",
    authorName: "",
    penName: "",
    seriesTitle: "",
    seriesNumber: "",
  },

  audience: {
    ageRange: "",
    readingArrangement: "",
    readingLevel: "",
    objective: "",
    themes: "",
    culturalSetting: "",
    schoolGrade: "",
  },

  bookForm: {
    type: "",
    customType: "",
    workingUnit: "",
  },

  visualFormat: {
    illustrationFrequency: "",
    visualStyle: "",
    colourAtmosphere: "",
    orientation: "",
    trimSize: "",
    customTrimSize: "",
    bleed: "",
    textPlacement: "",
    publicationFormat: "",
  },

  collaboration: {
    mode: "",
    optionalRoles: [],
  },

  bookMap: {
    units: [],
  },

  manuscriptPlan: {
    entries: [],
  },

  illustrationPlan: {
    entries: [],
  },

  coverPlan: {
    coverType: "front_only",
    editionLanguage: "en",
    title: "",
    subtitle: "",
    authorName: "",
    illustratorCredit: "",
    contributorCredits: "",
    visualBrief: "",
    featuredElements: "",
    settingAtmosphere: "",
    colourMood: "",
    illustrationStyle: "",
    textPlacement: "",
    backCoverImageRole: "",
    backCoverImageMode: "none",
    backCoverTitleMode: "auto",

    backCoverTitleFontFamily: "",
    backCoverTitleFontSize: "",
    backCoverTitleColour: "",
    backCoverTitleAlignment: "auto",
    backCoverTitleLineSpacing: "",

    backCoverBlurb: "",

    backCoverBlurbFontFamily: "",
    backCoverBlurbFontSize: "",
    backCoverBlurbColour: "",
    backCoverBlurbAlignment: "auto",
    backCoverBlurbLineSpacing: "",
    spineTitleMode: "auto",
    spineAuthorMode: "auto",
    spineText: "",
    rightsNote: "",
  },

  status: "local_draft",
};

function safeObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  )
    ? value
    : {};
}

export function persistenceSnapshot(
  draft = {}
) {
  const source =
    safeObject(draft);

  return JSON.stringify({
    projectPlan: {
      identity:
        safeObject(source.identity),

      audience:
        safeObject(source.audience),

      bookForm:
        safeObject(source.bookForm),

      visualFormat:
        safeObject(source.visualFormat),

      collaboration:
        safeObject(source.collaboration),
    },

    bookMap:
      safeObject(source.bookMap),

    manuscriptPlan:
      safeObject(source.manuscriptPlan),

    illustrationPlan:
      safeObject(source.illustrationPlan),

    coverPlan:
      safeObject(source.coverPlan),

    publicationReadiness:
      safeObject(
        source.publicationReadiness
      ),

    blueprintProgress:
      safeObject(
        source.blueprintProgress
      ),
  });
}

export function hydrateChildrenBookDraft(
  persisted = {}
) {
  const container =
    safeObject(persisted);

  const blueprint =
    safeObject(
      container.blueprint ||
      container
    );

  const projectPlan =
    safeObject(
      blueprint.projectPlan
    );

  return {
    ...EMPTY_DRAFT,

    identity: {
      ...EMPTY_DRAFT.identity,
      ...safeObject(
        projectPlan.identity
      ),
    },

    audience: {
      ...EMPTY_DRAFT.audience,
      ...safeObject(
        projectPlan.audience
      ),
    },

    bookForm: {
      ...EMPTY_DRAFT.bookForm,
      ...safeObject(
        projectPlan.bookForm
      ),
    },

    visualFormat: {
      ...EMPTY_DRAFT.visualFormat,
      ...safeObject(
        projectPlan.visualFormat
      ),
    },

    collaboration: {
      ...EMPTY_DRAFT.collaboration,
      ...safeObject(
        projectPlan.collaboration
      ),

      optionalRoles:
        Array.isArray(
          projectPlan
            .collaboration
            ?.optionalRoles
        )
          ? projectPlan
              .collaboration
              .optionalRoles
          : [],
    },

    bookMap: {
      ...EMPTY_DRAFT.bookMap,
      ...safeObject(
        blueprint.bookMap
      ),

      units:
        Array.isArray(
          blueprint.bookMap?.units
        )
          ? blueprint.bookMap.units
          : [],
    },

    manuscriptPlan: {
      ...EMPTY_DRAFT.manuscriptPlan,
      ...safeObject(
        blueprint.manuscriptPlan
      ),

      entries:
        Array.isArray(
          blueprint
            .manuscriptPlan
            ?.entries
        )
          ? blueprint
              .manuscriptPlan
              .entries
          : [],
    },

    illustrationPlan: {
      ...EMPTY_DRAFT.illustrationPlan,
      ...safeObject(
        blueprint.illustrationPlan
      ),

      entries:
        Array.isArray(
          blueprint
            .illustrationPlan
            ?.entries
        )
          ? blueprint
              .illustrationPlan
              .entries
          : [],
    },

    coverPlan: {
      ...EMPTY_DRAFT.coverPlan,
      ...safeObject(
        blueprint.coverPlan
      ),
    },

    publicationReadiness:
      safeObject(
        blueprint
          .publicationReadiness
      ),

    blueprintProgress:
      safeObject(
        blueprint.blueprintProgress
      ),

    status:
      container.project ||
      container.blueprint
        ? "persisted_draft"
        : "local_draft",
  };
}

export function prepareChildrensBookManuscriptUnitDivisionApplication({
  draft = {},
  decisions = {},
  childrensBookAssets = [],
} = {}) {
  const sourceDraft =
    safeObject(draft);

  const reviewedDecisions =
    safeObject(decisions);

  const proposal =
    buildChildrensBookManuscriptUnitDivisionProposal({
      projectPlan:
        sourceDraft,

      sourceUnitId:
        reviewedDecisions
          .sourceUnitId,

      segments:
        reviewedDecisions
          .segments,

      unitTypes:
        reviewedDecisions
          .unitTypes,

      illustrationSegmentIndex:
        reviewedDecisions
          .illustrationSegmentIndex,

      childrensBookAssets,
    });

  if (
    !proposal.ok ||
    !proposal.readyToApply
  ) {
    return {
      ok: false,
      readyToApply: false,

      status: 0,

      code:
        proposal.code ||
        "division_not_ready",

      error:
        proposal.error ||
        "The division review is no longer ready. Review the proposed pages before applying it.",
    };
  }

  const nextBookMap =
    safeObject(
      proposal.nextBookMap
    );

  const nextManuscriptPlan =
    safeObject(
      proposal.nextManuscriptPlan
    );

  const nextIllustrationPlan =
    safeObject(
      proposal.nextIllustrationPlan
    );

  if (
    !Array.isArray(
      nextBookMap.units
    ) ||
    !Array.isArray(
      nextManuscriptPlan.entries
    ) ||
    !Array.isArray(
      nextIllustrationPlan.entries
    )
  ) {
    return {
      ok: false,
      readyToApply: false,
      status: 0,
      code:
        "division_result_invalid",
      error:
        "The divided book structure is incomplete and cannot be applied safely.",
    };
  }

  return {
    ok: true,
    readyToApply: true,
    proposal,

    nextDraft: {
      ...sourceDraft,
      bookMap:
        nextBookMap,
      manuscriptPlan:
        nextManuscriptPlan,
      illustrationPlan:
        nextIllustrationPlan,
    },
  };
}

export async function applyChildrensBookManuscriptUnitDivisionApplication({
  draft = {},
  decisions = {},
  childrensBookAssets = [],
  saveDraft,
  isFR = false,
} = {}) {
  const prepared =
    prepareChildrensBookManuscriptUnitDivisionApplication({
      draft,
      decisions,
      childrensBookAssets,
    });

  if (!prepared.ok) {
    return {
      ...prepared,

      error:
        isFR
          ? "La révision de la division n’est plus prête. Vérifiez les pages proposées avant de l’appliquer."
          : prepared.error,
    };
  }

  if (
    typeof saveDraft !==
    "function"
  ) {
    return {
      ok: false,
      persisted: false,
      status: 0,
      code:
        "division_save_unavailable",

      error:
        isFR
          ? "Ce livre ne peut pas enregistrer cette division pour le moment."
          : "This book cannot save this division right now.",
    };
  }

  const saved =
    await saveDraft(
      prepared.nextDraft
    );

  if (!saved?.ok) {
    return saved || {
      ok: false,
      persisted: false,
      status: 0,
      code:
        "division_save_failed",

      error:
        isFR
          ? "La division n’a pas pu être enregistrée. Aucune modification n’a été appliquée."
          : "The division could not be saved. No changes were applied.",
    };
  }

  const summary =
    prepared.proposal.summary ||
    {};

  return {
    ...saved,

    applicationSummary: {
      ...summary,

      sourceUnitId:
        String(
          decisions
            ?.sourceUnitId ||
          ""
        ).trim(),

      segmentCount:
        prepared.proposal
          .proposedSegments
          .length,

      destinationLabels:
        prepared.proposal
          .proposedSegments
          .map(
            (segment) =>
              segment
                .destinationLabel
          ),
    },
  };
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  children,
  required = false,
  guidance = "",
  isFR = false,
}) {
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    minHeight: 46,
    padding: "11px 12px",
    borderRadius: 10,
    border: "1px solid #cbd5e1",
    fontFamily: "inherit",
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1.45,
    color: "#1e293b",
    background: "#ffffff",
  };

  return (
    <label
      style={{
        display: "grid",
        gap: 7,
        color: "#334155",
      }}
    >
      <span
        style={{
          fontSize: 15,
          fontWeight: 900,
          lineHeight: 1.35,
        }}
      >
        {label}

        {required ? (
          <span
            aria-label={
              isFR
                ? "champ obligatoire"
                : "required field"
            }
            style={{
              color: "#dc2626",
              marginLeft: 4,
            }}
          >
            *
          </span>
        ) : null}
      </span>

      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.45,
          color: "#64748b",
        }}
      >
        {guidance ||
          (
            required
              ? (
                  isFR
                    ? "Champ obligatoire."
                    : "Required field."
                )
              : (
                  isFR
                    ? "Facultatif."
                    : "Optional."
                )
          )}
      </span>

      {children || (
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          required={required}
          onChange={(event) =>
            onChange(event.target.value)
          }
          style={inputStyle}
        />
      )}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required = false,
  guidance = "",
  isFR = false,
}) {
  return (
    <Field
      label={label}
      required={required}
      guidance={guidance}
      isFR={isFR}
    >
      <select
        value={value}
        required={required}
        onChange={(event) =>
          onChange(event.target.value)
        }
        style={{
          width: "100%",
          minHeight: 46,
          padding: "11px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#ffffff",
          fontFamily: "inherit",
          fontSize: 15,
          fontWeight: 500,
          color: "#1e293b",
        }}
      >
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export default function ChildrensBookCreationFlow({
  lang = "en",
  onExit,
  initialDraft = null,
  persistenceEnabled = false,
  readOnly = false,
  projectId = "",
  revision = 1,
  onSaveProject,
  onApproveChildrensBookEdition,
  onUpdateChildrensBookVisibility,
  onLoadChildrensBookAssemblyRange,
  onLoadChildrensBookAssemblyUnit,
  onLoadChildrensBookPublicationCompatibility,
  onLoadChildrensBookPublicationProduction,
  onLoadChildrensBookCoverPalettePreview,
  onLoadChildrensBookPublicationInteriorReview,
  onLoadChildrensBookPublicationCoverReview,
  onLoadChildrensBookPublicationFinalInterior,
  onLoadChildrensBookPublicationFinalCover,
  onRegenerateChildrensBookPublicationInterior,
  onApproveChildrensBookPublicationInterior,
  onRegenerateChildrensBookPublicationCover,
  onApproveChildrensBookPublicationCover,
  onStartChildrensBookPublicationProduction,
  onPrepareChildrensBookLocalDraftReflow,
  onPreviewManuscriptImport,
  childrensBookAssets = [],
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  childrensBookAssetActionId = "",
  onLoadChildrensBookAssetFile,
  onUploadChildrensBookCover,
  onUploadChildrensBookIllustration,
  onRegisterChildrensBookAsset,
  onReplaceChildrensBookAsset,
  onRemoveChildrensBookAsset,
}) {
  const isFR =
    String(lang).toLowerCase() === "fr";

  const initialWorkspace =
    initialChildrenBookWorkspace();
  const useUnifiedBookSetupV1E5 = persistenceEnabled &&
    process.env.REACT_APP_CHILDRENS_BOOK_NOVEL_REUSE_V1E4D_ENABLED === "true";
  const v1e4dEnabled = process.env.REACT_APP_CHILDRENS_BOOK_NOVEL_REUSE_V1E4D_ENABLED === "true" &&
    Boolean(String(projectId || "").trim()) && persistenceEnabled;
  const [v1e4dBookActive, setV1e4dBookActive] = useState(
    () => v1e4dEnabled && initialWorkspace === "manuscript-plan"
  );

  const [step, setStep] = useState(0);

  const [reviewing, setReviewing] =
    useState(
      initialWorkspace ===
        "project-plan-review"
    );

  const [planningCover, setPlanningCover] =
    useState(
      initialWorkspace === "cover-plan"
    );

  const [planningBookMap, setPlanningBookMap] =
    useState(
      initialWorkspace === "book-map"
    );

  const [planningManuscript, setPlanningManuscript] =
    useState(
      initialWorkspace ===
        "manuscript-plan"
    );

  const [planningIllustrations, setPlanningIllustrations] =
    useState(
      initialWorkspace ===
        "illustration-plan"
    );

  const [viewingBookAssembly, setViewingBookAssembly] =
    useState(
      initialWorkspace ===
        "book-assembly"
    );

  const [viewingBookPreview, setViewingBookPreview] =
    useState(
      initialWorkspace ===
        "book-preview"
    );

  const [reviewingPublicationReadiness, setReviewingPublicationReadiness] =
    useState(
      initialWorkspace ===
        "publication-readiness"
    );

  const [viewingPublicationProof, setViewingPublicationProof] =
    useState(
      initialWorkspace ===
        "publication-proof"
    );

  const [viewingPublicationHandoff, setViewingPublicationHandoff] =
    useState(
      initialWorkspace ===
        "publication-handoff"
    );

  const [
    viewingPublishedBook,
    setViewingPublishedBook,
  ] = useState(
    initialWorkspace ===
      "published-book"
  );

  const [viewingBlueprint, setViewingBlueprint] =
    useState(
      initialWorkspace === "blueprint"
    );

  const [draft, setDraft] = useState(
    () =>
      hydrateChildrenBookDraft(
        initialDraft || {}
      )
  );

  /*
   * Local measured reflow is asynchronous.
   *
   * Keep both request order and the latest rendered
   * authoring-draft signature so an older response can
   * never overwrite edits the writer made while physical
   * measurement was in flight.
   */
  const localDraftReflowRequestSequenceRef =
    useRef(0);

  const latestLocalDraftReflowSignatureRef =
    useRef("");

  latestLocalDraftReflowSignatureRef.current =
    JSON.stringify(
      draft
    );

  const [
    lastSavedSnapshot,
    setLastSavedSnapshot,
  ] = useState(() => {
    if (!projectId) {
      return "";
    }

    return persistenceSnapshot(
      hydrateChildrenBookDraft(
        initialDraft || {}
      )
    );
  });

  const [persistedAudience, setPersistedAudience] = useState(() =>
    projectId ? {...safeObject(hydrateChildrenBookDraft(initialDraft || {}).audience)} : {}
  );

  const [
    currentProjectId,
    setCurrentProjectId,
  ] = useState(
    String(projectId || "").trim()
  );

  const [
    currentRevision,
    setCurrentRevision,
  ] = useState(() => {
    const value = Number(revision);

    return (
      Number.isInteger(value) &&
      value > 0
    )
      ? value
      : 1;
  });

  const savedAudienceReady = Boolean(currentProjectId) &&
    childrensBookSavedAudienceReady(persistedAudience);

  const [persisting, setPersisting] =
    useState(false);

  const [
    persistenceMessageKey,
    setPersistenceMessageKey,
  ] = useState("");

  const [
    persistenceError,
    setPersistenceError,
  ] = useState("");

  const [savedSteps, setSavedSteps] =
    useState([false, false, false, false, false]);

  const [stepMessage, setStepMessage] =
    useState("");

  const [
    wholeBookReviewContext,
    setWholeBookReviewContext,
  ] = useState(
    () =>
      wholeBookReviewContextFromSearch()
  );

  const [
    manuscriptPlanPosition,
    setManuscriptPlanPosition,
  ] = useState({
    unitId: "",
    rangeStart: 0,
  });

  const currentWorkspace = (() => {
    if (v1e4dEnabled && v1e4dBookActive) return "manuscript-plan";
    if (planningBookMap) {
      return "book-map";
    }

    if (planningManuscript) {
      return "manuscript-plan";
    }

    if (planningIllustrations) {
      return "illustration-plan";
    }

    if (viewingBookAssembly) {
      return "book-assembly";
    }

    if (viewingBookPreview) {
      return "book-preview";
    }

    if (
      reviewingPublicationReadiness
    ) {
      return "publication-readiness";
    }

    if (
      viewingPublicationProof
    ) {
      return "publication-proof";
    }

    if (
      viewingPublicationHandoff
    ) {
      return "publication-handoff";
    }

    if (
      viewingPublishedBook
    ) {
      return "published-book";
    }

    if (planningCover) {
      return "cover-plan";
    }

    if (viewingBlueprint) {
      return "blueprint";
    }

    if (reviewing) {
      return "project-plan-review";
    }

    return "project-plan";
  })();

  function openWorkspace(
    workspace,
    {
      preserveWholeBookReviewContext = false,
    } = {}
  ) {
    if (v1e4dEnabled && workspace === "novel-reuse-workspace-v1e4d") {
      setWholeBookReviewContext(null);
      replaceChildrenBookWorkspacePath("manuscript-plan", currentProjectId);
      setV1e4dBookActive(true);
      return;
    }
    if (useUnifiedBookSetupV1E5 && workspace === "project-plan-audience-v1e4x") {
      replaceChildrenBookWorkspacePath("book-setup", currentProjectId);
    }
    setV1e4dBookActive(false);
    if (
      !preserveWholeBookReviewContext
    ) {
      setWholeBookReviewContext(
        null
      );
    }

    setPlanningBookMap(false);
    setPlanningManuscript(false);
    setPlanningIllustrations(false);
    setViewingBookAssembly(false);
    setViewingBookPreview(false);
    setReviewingPublicationReadiness(
      false
    );
    setViewingPublicationProof(false);
    setViewingPublicationHandoff(
      false
    );
    setViewingPublishedBook(false);
    setPlanningCover(false);
    setViewingBlueprint(false);
    setReviewing(false);

    if (workspace === "book-map") {
      setPlanningBookMap(true);
      return;
    }

    if (
      workspace ===
      "manuscript-plan"
    ) {
      setPlanningManuscript(true);
      return;
    }

    if (
      workspace ===
      "illustration-plan"
    ) {
      setPlanningIllustrations(true);
      return;
    }

    if (
      workspace ===
      "book-assembly"
    ) {
      setViewingBookAssembly(true);
      return;
    }

    if (
      workspace ===
      "book-preview"
    ) {
      setViewingBookPreview(true);
      return;
    }

    if (
      workspace ===
      "publication-readiness"
    ) {
      setReviewingPublicationReadiness(
        true
      );
      return;
    }

    if (
      workspace ===
      "publication-proof"
    ) {
      setViewingPublicationProof(
        true
      );
      return;
    }

    if (
      workspace ===
      "publication-handoff"
    ) {
      setViewingPublicationHandoff(
        true
      );
      return;
    }

    if (
      workspace ===
      "published-book"
    ) {
      setViewingPublishedBook(
        true
      );
      return;
    }

    if (workspace === "cover-plan") {
      setPlanningCover(true);
      return;
    }

    if (workspace === "blueprint") {
      setViewingBlueprint(true);
      return;
    }

    if (
      workspace ===
      "project-plan-review"
    ) {
      setReviewing(true);
      return;
    }

    setStep(workspace === "project-plan-audience-v1e4x" ? 1 : 0);
  }

  function openPublicationProofManuscriptCorrection(
    context = {}
  ) {
    const unitId =
      String(
        context?.unitId || ""
      ).trim();

    if (unitId) {
      setManuscriptPlanPosition({
        unitId,
        rangeStart: 0,
      });
    }

    openWorkspace(
      "manuscript-plan"
    );
  }

  function openWholeBookCorrection(
    workspace,
    context = {}
  ) {
    setWholeBookReviewContext({
      origin:
        WHOLE_BOOK_REVIEW_ORIGIN,

      unitId:
        String(
          context?.unitId || ""
        ).trim(),

      windowStart:
        normalizedWholeBookReviewWindowStart(
          context?.windowStart
        ),
    });

    openWorkspace(
      workspace,
      {
        preserveWholeBookReviewContext:
          true,
      }
    );
  }

  function openBookMapFromCorrectionWorkspace() {
    if (
      wholeBookReviewContext
    ) {
      openWholeBookCorrection(
        "book-map",
        wholeBookReviewContext
      );

      return;
    }

    openWorkspace(
      "book-map"
    );
  }

  function returnToWholeBookReview() {
    openWorkspace(
      "book-assembly",
      {
        preserveWholeBookReviewContext:
          true,
      }
    );
  }

  useEffect(() => {
    replaceChildrenBookWorkspacePath(
      currentWorkspace,
      currentProjectId,
      wholeBookReviewContext
    );
  }, [
    currentWorkspace,
    currentProjectId,
    wholeBookReviewContext,
  ]);

  const steps = useMemo(
    () => (
      isFR
        ? [
            "Nommez votre livre",
            "Choisissez le public",
            "Choisissez la forme du livre",
            "Planifiez le format visuel",
            "Choisissez votre mode de travail",
          ]
        : [
            "Name Your Book",
            "Choose the Audience",
            "Choose the Book Form",
            "Plan the Visual Format",
            "Choose How You Will Work",
          ]
    ),
    [isFR]
  );

  async function applyReviewedManuscriptImport({
    sections = [],
    mappingBySection = {},
    decisionBySection = {},
    replacementConfirmed = false,
  } = {}) {
    const prepared =
      prepareChildrensBookManuscriptImport({
        sections,

        bookMapUnits:
          draft.bookMap?.units,

        manuscriptPlan:
          draft.manuscriptPlan,

        mappingBySection,
        decisionBySection,
      });

    const plan =
      prepared.ok
        ? prepared.plan
        : null;

    if (
      !plan ||
      Number(
        plan.summary?.imported ||
          0
      ) < 1
    ) {
      return {
        ok: false,
        persisted: false,

        error: isFR
          ? "La vérification du manuscrit n’est plus prête. Revoyez les choix des sections avant l’import."
          : "The manuscript review is no longer ready. Review the section choices before importing.",
      };
    }

    if (
      plan
        .requiresReplacementConfirmation &&
      replacementConfirmed !== true
    ) {
      return {
        ok: false,
        persisted: false,

        error: isFR
          ? "Confirmez le remplacement du texte actuel avant l’import."
          : "Confirm replacement of the current text before importing.",
      };
    }

    const nextManuscriptPlan =
      plan.nextManuscriptPlan;

    if (
      !nextManuscriptPlan ||
      typeof nextManuscriptPlan !==
        "object" ||
      Array.isArray(
        nextManuscriptPlan
      ) ||
      !Array.isArray(
        nextManuscriptPlan.entries
      )
    ) {
      return {
        ok: false,
        persisted: false,

        error: isFR
          ? "Le manuscrit importé n’est pas prêt à être appliqué."
          : "The imported manuscript is not ready to be applied.",
      };
    }

    const nextDraft = {
      ...draft,

      manuscriptPlan:
        nextManuscriptPlan,
    };

    if (!persistenceEnabled) {
      setDraft(
        nextDraft
      );

      return {
        ok: true,
        persisted: false,

        applicationSummary:
          plan.summary,

        draft:
          nextDraft,
      };
    }

    if (
      typeof onSaveProject !==
      "function"
    ) {
      return {
        ok: false,
        persisted: false,

        error: isFR
          ? "Ce livre ne peut pas être enregistré pour le moment."
          : "This book cannot be saved right now.",
      };
    }

    const result =
      await saveDraftSnapshot(
        nextDraft
      );

    if (!result?.ok) {
      return result;
    }

    return {
      ...result,

      applicationSummary:
        plan.summary,
    };
  }

  async function applyReviewedManuscriptUnitDivision(
    decisions = {}
  ) {
    return applyChildrensBookManuscriptUnitDivisionApplication({
      draft,
      decisions,
      childrensBookAssets,
      saveDraft:
        saveDraftSnapshot,
      isFR,
    });
  }

  async function applyBookStructureStructuralManuscriptTransition(
    request = {}
  ) {
    const normalizedRequest =
      safeObject(
        request
      );

    const changedUnitId =
      String(
        normalizedRequest
          .changedUnitId ||
        ""
      ).trim();

    const nextUnit =
      safeObject(
        normalizedRequest
          .nextUnit
      );

    const authoringImageGeometry =
      Object.prototype
        .hasOwnProperty.call(
          normalizedRequest,
          "authoringImageGeometry"
        )
        ? normalizedRequest
            .authoringImageGeometry
        : null;

    /*
     * Browser-local page-form concurrency authority.
     *
     * Read this callback from the original request rather
     * than the sanitized request object. It is UI-only
     * authority and must never enter the backend payload.
     */
    const isRequestCurrent =
      typeof request
        ?.isRequestCurrent ===
          "function"
        ? request
            .isRequestCurrent
        : null;

    if (
      !changedUnitId ||
      !Object.keys(
        nextUnit
      ).length
    ) {
      return {
        ok: false,
        applicable: false,
        readyToApply: false,
        applied: false,
        persisted: false,

        code:
          "local_reflow_request_invalid",

        error:
          isFR
            ? "La modification de cette page n’est pas prête à être vérifiée."
            : "This page change is not ready for manuscript reflow.",
      };
    }

    if (
      typeof onPrepareChildrensBookLocalDraftReflow !==
        "function"
    ) {
      return {
        ok: false,
        applicable: false,
        readyToApply: false,
        applied: false,
        persisted: false,

        code:
          "local_reflow_proposal_unavailable",

        error:
          isFR
            ? "La vérification physique du déplacement du manuscrit n’est pas disponible pour le moment."
            : "Physical manuscript reflow is not available right now.",
      };
    }

    /*
     * Send the exact current in-memory writer draft.
     *
     * Do not reload the persisted Blueprint here:
     * Save Page Changes may intentionally operate on work
     * that has not yet been persisted by Save Blueprint.
     */
    const requestDraft =
      draft;

    const requestDraftSignature =
      JSON.stringify(
        requestDraft
      );

    const requestSequence =
      localDraftReflowRequestSequenceRef
        .current +
      1;

    localDraftReflowRequestSequenceRef.current =
      requestSequence;

    let result;

    try {
      result =
        await onPrepareChildrensBookLocalDraftReflow({
          draft:
            requestDraft,

          changedUnitId,

          proposedUnit:
            nextUnit,

          authoringImageGeometry,
        });
    } catch (error) {
      return {
        ok: false,
        applicable: true,
        readyToApply: false,
        applied: false,
        persisted: false,

        code:
          "local_reflow_proposal_request_failed",

        error:
          String(
            error?.message ||
            (
              isFR
                ? "Le déplacement physique du manuscrit n’a pas pu être préparé."
                : "The physical manuscript reflow could not be prepared."
            )
          ),
      };
    }

    /*
     * Two independent stale-response guards:
     *
     * 1. a later reflow request supersedes this one;
     * 2. any newly rendered authoring draft supersedes
     *    the exact draft snapshot that was measured.
     */
    if (
      requestSequence !==
        localDraftReflowRequestSequenceRef
          .current ||
      latestLocalDraftReflowSignatureRef
        .current !==
        requestDraftSignature
    ) {
      return {
        ok: false,
        applicable: true,
        readyToApply: false,
        applied: false,
        persisted: false,
        stale: true,

        code:
          "local_reflow_response_stale",

        error:
          isFR
            ? "La page a changé pendant la vérification physique. Enregistrez de nouveau les modifications de la page."
            : "The page changed while physical reflow was being checked. Save Page Changes again.",
      };
    }

    if (
      !result ||
      result.ok === false
    ) {
      return result || {
        ok: false,
        applicable: true,
        readyToApply: false,
        applied: false,
        persisted: false,

        code:
          "local_reflow_proposal_failed",

        error:
          isFR
            ? "Le déplacement physique du manuscrit n’a pas pu être préparé."
            : "The physical manuscript reflow could not be prepared.",
      };
    }

    /*
     * This endpoint is computational only.
     * Fail closed if persistence authority ever appears
     * in its response contract unexpectedly.
     */
    if (
      result.persisted ===
        true
    ) {
      return {
        ok: false,
        applicable: true,
        readyToApply: false,
        applied: false,
        persisted: false,

        code:
          "local_reflow_unexpected_persistence",

        error:
          isFR
            ? "La vérification du manuscrit a renvoyé une autorité d’enregistrement inattendue."
            : "The manuscript reflow returned unexpected persistence authority.",
      };
    }

    /*
     * Routine page insertion/reflow is an automatic
     * consequence of the writer's layout choice.
     * It must not recreate the retired second decision.
     */
    if (
      result.confirmationRequired ===
        true
    ) {
      return {
        ok: false,
        applicable: true,
        readyToApply: false,
        applied: false,
        persisted: false,

        code:
          "local_reflow_unexpected_confirmation",

        error:
          isFR
            ? "La vérification du manuscrit a demandé une confirmation inattendue."
            : "The manuscript reflow unexpectedly requested confirmation.",
      };
    }

    /*
     * Not every structural edit reduces physical text
     * capacity. In that case Book Structure continues
     * through its existing ordinary onChange save path.
     */
    if (
      result.applicable ===
        false
    ) {
      return {
        ...result,

        applied: false,
        persisted: false,
      };
    }

    const nextDraft =
      result.nextDraft;

    if (
      result.readyToApply !==
        true ||
      !nextDraft ||
      typeof nextDraft !==
        "object" ||
      Array.isArray(
        nextDraft
      )
    ) {
      return {
        ok: false,
        applicable: true,
        readyToApply: false,
        applied: false,
        persisted: false,

        code:
          "local_reflow_next_draft_invalid",

        error:
          isFR
            ? "Le déplacement physique du manuscrit n’est pas prêt à être appliqué."
            : "The physical manuscript reflow is not ready to be applied.",
      };
    }

    /*
     * Atomic in-memory adoption only.
     *
     * Save Blueprint remains the explicit persistence
     * authority exactly as before.
     */
    /*
     * Final child-local currency check immediately before
     * atomic parent draft adoption.
     */
    if (
      isRequestCurrent
    ) {
      let requestStillCurrent =
        false;

      try {
        requestStillCurrent =
          isRequestCurrent() ===
            true;
      } catch (_error) {
        requestStillCurrent =
          false;
      }

      if (
        !requestStillCurrent
      ) {
        return {
          ok: false,
          applicable: true,
          readyToApply: false,
          applied: false,
          persisted: false,
          stale: true,

          code:
            "local_reflow_child_request_stale",

          error:
            isFR
              ? "La page a changé pendant la vérification physique. Vos modifications les plus récentes ont été conservées. Enregistrez de nouveau les modifications de la page."
              : "The page changed while physical reflow was being checked. Your newer edits were kept. Save Page Changes again.",
        };
      }
    }

    setDraft(
      nextDraft
    );

    latestLocalDraftReflowSignatureRef.current =
      JSON.stringify(
        nextDraft
      );

    return {
      ...result,

      applied: true,
      persisted: false,

      draft:
        nextDraft,
    };
  }

  async function saveDraftSnapshot(
    draftToSave
  ) {
    if (
      !persistenceEnabled ||
      typeof onSaveProject !==
        "function"
    ) {
      return;
    }

    const currentSnapshot =
      persistenceSnapshot(
        draftToSave
      );

    if (
      currentProjectId &&
      currentSnapshot ===
        lastSavedSnapshot
    ) {
      setPersistenceError("");

      setPersistenceMessageKey(
        "unchanged"
      );

      return;
    }

    try {
      setPersisting(true);
      setPersistenceError("");
      setPersistenceMessageKey("");

      const result =
        await onSaveProject(
          draftToSave,
          {
            projectId:
              currentProjectId,

            expectedRevision:
              currentRevision,
          }
        );

      const nextProjectId =
        String(
          result?.project?.id ||
          result?.projectId ||
          currentProjectId ||
          ""
        ).trim();

      const nextRevision =
        Number(
          result
            ?.blueprint
            ?.revision ||
          result?.revision ||
          currentRevision
        );

      if (nextProjectId) {
        setCurrentProjectId(
          nextProjectId
        );
      }

      if (
        Number.isInteger(
          nextRevision
        ) &&
        nextRevision > 0
      ) {
        setCurrentRevision(
          nextRevision
        );
      }

      const savedDraft = {
        ...draftToSave,

        status:
          "persisted_draft",
      };

      setDraft(
        savedDraft
      );

      setLastSavedSnapshot(
        currentSnapshot
      );
      // Only a confirmed Blueprint save can unlock Scene writing.
      setPersistedAudience({...safeObject(draftToSave.audience)});

      setPersistenceMessageKey(
        "saved"
      );

      return {
        ok: true,
        persisted: true,
        result,
        draft:
          savedDraft,
      };
    } catch (error) {
      const message =
        String(
          error?.message ||
          (
            isFR
              ? "Impossible d’enregistrer le projet."
              : "The project could not be saved."
          )
        ).trim();

      setPersistenceError(
        message
      );

      return {
        ok: false,
        persisted: false,

        status:
          Number(
            error?.status || 0
          ) || 0,

        error:
          message,
      };
    } finally {
      setPersisting(false);
    }
  }


  async function savePersistentProject() {
    return saveDraftSnapshot(
      draft
    );
  }

  async function publishChildrensBookOnNextScenesLegacy() {
    const publishedAt =
      new Date().toISOString();

    const nextDraft = {
      ...draft,

      publicationReadiness: {
        ...draft.publicationReadiness,

        status:
          "published_nextscenes",

        destination:
          "nextscenes",

        publishedAt,
      },
    };

    if (!persistenceEnabled) {
      setDraft(
        nextDraft
      );

      return {
        ok: true,
        persisted: false,
        publishedAt,
        draft: nextDraft,
      };
    }

    if (
      typeof onSaveProject !==
        "function"
    ) {
      return {
        ok: false,
        persisted: false,

        error: isFR
          ? "Ce livre ne peut pas être enregistré pour le moment."
          : "This book cannot be saved right now.",
      };
    }

    const result =
      await saveDraftSnapshot(
        nextDraft
      );

    if (result?.ok === false) {
      return result;
    }

    return {
      ok: true,
      persisted: true,
      publishedAt,
      draft: nextDraft,
    };
  }

  async function publishChildrensBookOnNextScenes() {
    if (process.env.REACT_APP_CHILDRENS_BOOK_APPROVED_EDITION_V1_ENABLED !== "true") {
      return publishChildrensBookOnNextScenesLegacy();
    }
    if (!persistenceEnabled || !currentProjectId ||
        typeof onApproveChildrensBookEdition !== "function") {
      return { ok: false, error: "Publication approval is unavailable." };
    }
    // Never publish an unsaved working manuscript or allow browser-authored
    // publication status to substitute for a server-owned approval.
    if (persistenceSnapshot(draft) !== lastSavedSnapshot) {
      return { ok: false, error: "Save the manuscript before publishing." };
    }
    const approved = await onApproveChildrensBookEdition(currentProjectId,currentRevision);
    if (!approved?.ok) {
      return {ok:false,error:approved?.error||approved?.code||"Publication approval failed."};
    }
    const publishedAt = new Date().toISOString();
    setDraft(current=>({...current,publicationReadiness:{
      ...current.publicationReadiness,status:"published_nextscenes",
      destination:"nextscenes",publishedAt,
    }}));
    return {ok:true,persisted:true,publishedAt,editionNumber:approved.editionNumber};
  }

  function renderPersistenceControls() {
    if (!persistenceEnabled) {
      return null;
    }

    const hasProject =
      Boolean(currentProjectId);

    const hasUnsavedChanges =
      persistenceSnapshot(draft) !==
      lastSavedSnapshot;

    const statusKey =
      persistenceError
        ? "error"
        : persisting
          ? "saving"
          : hasUnsavedChanges
            ? "unsaved"
            : persistenceMessageKey ||
              (
                hasProject
                  ? "up_to_date"
                  : "new_project"
              );

    const statusText = {
      error:
        persistenceError,

      saving:
        isFR
          ? "Enregistrement du Blueprint…"
                    : (useUnifiedBookSetupV1E5 ? "Saving changes…" : "Saving Blueprint…"),

      unsaved:
        isFR
          ? "Modifications non enregistrées"
          : "Unsaved changes",

      saved:
        isFR
          ? "Blueprint enregistré"
                    : (useUnifiedBookSetupV1E5 ? "Changes saved" : "Blueprint saved"),

      unchanged:
        isFR
          ? "Aucune modification à enregistrer."
          : "No changes to save.",

      up_to_date:
        isFR
          ? (useUnifiedBookSetupV1E5 ? "Toutes les modifications sont enregistrées." : "Le Blueprint est à jour.")
                    : (useUnifiedBookSetupV1E5 ? "All changes saved." : "The Blueprint is up to date."),

      new_project:
        isFR
          ? "Le premier enregistrement créera le projet."
          : "The first save will create the project.",
    }[statusKey];

    const statusStyle = {
      error: {
        background: "#fef2f2",
        border:
          "1px solid rgba(185,28,28,0.30)",
        color: "#991b1b",
      },

      saving: {
        background: "#fff7ed",
        border:
          "1px solid rgba(194,65,12,0.30)",
        color: "#9a3412",
      },

      unsaved: {
        background: "#fff7ed",
        border:
          "1px solid rgba(194,65,12,0.30)",
        color: "#9a3412",
      },

      saved: {
        background: "#ecfdf5",
        border:
          "1px solid rgba(22,101,52,0.30)",
        color: "#166534",
      },

      unchanged: {
        background: "#ecfdf5",
        border:
          "1px solid rgba(22,101,52,0.30)",
        color: "#166534",
      },

      up_to_date: {
        background: "#ecfdf5",
        border:
          "1px solid rgba(22,101,52,0.30)",
        color: "#166534",
      },

      new_project: {
        background: "#eff6ff",
        border:
          "1px solid rgba(37,99,235,0.30)",
        color: "#1d4ed8",
      },
    }[statusKey];

    return (
      <section
        className="ns-card"
        aria-label={
          isFR
            ? "État d’enregistrement du projet"
            : "Project save status"
        }
        style={{
          padding: 18,
          marginBottom: 20,
          borderRadius: 18,
          background: "#f8fbff",
          border:
            "2px solid rgba(37,99,235,0.32)",
          boxShadow:
            "0 8px 22px rgba(15,23,42,0.08)",
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 1fr) auto",
          alignItems: "center",
          gap: 18,
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <strong
              style={{
                fontSize: 18,
                color: "#172033",
              }}
            >
              {hasProject
                ? (
                    isFR
                      ? "Projet enregistré"
                      : "Saved project"
                  )
                : (
                    isFR
                      ? "Nouveau projet"
                      : "New project"
                  )}
            </strong>

            {hasProject ? (
              <span
                style={{
                  padding: "4px 9px",
                  borderRadius: 999,
                  background: "#dbeafe",
                  color: "#1e40af",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {isFR
                  ? `Révision ${currentRevision}`
                  : `Revision ${currentRevision}`}
              </span>
            ) : null}
          </div>

          <div
            role={
              statusKey === "error"
                ? "alert"
                : "status"
            }
            aria-live="polite"
            aria-atomic="true"
            style={{
              ...statusStyle,
              marginTop: 12,
              padding: "11px 13px",
              borderRadius: 12,
            }}
          >
            <span
              style={{
                display: "block",
                marginBottom: 3,
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {isFR
                ? "Dernière mise à jour"
                : "Latest update"}
            </span>

            <strong>
              {statusText}
            </strong>
          </div>
        </div>

        <button
          className="ns-button ns-button-primary"
          type="button"
          disabled={persisting}
          aria-busy={persisting}
          onClick={
            savePersistentProject
          }
          style={{
            minWidth: 190,
            minHeight: 44,
          }}
        >
          {persisting
            ? (
                isFR
                  ? "Enregistrement…"
                  : "Saving…"
              )
            : hasProject
              ? (
                  isFR
                    ? (useUnifiedBookSetupV1E5 ? "Enregistrer" : "Enregistrer le Blueprint")
                    : (useUnifiedBookSetupV1E5 ? "Save Changes" : "Save Blueprint")
                )
              : (
                  isFR
                    ? "Créer et enregistrer"
                    : "Create and Save"
                )}
        </button>
      </section>
    );
  }

  function withPersistenceControls(
    content
  ) {
    return (
      <>
        {renderPersistenceControls()}

        {v1e4dEnabled && !v1e4dBookActive && currentWorkspace !== "project-plan" ? (
          <button className="ns-button ns-button-outline" type="button"
            data-testid="childrens-open-real-novel-book-workspace"
            onClick={() => openWorkspace("novel-reuse-workspace-v1e4d")}>
            {isFR ? "Retour à l’espace Livre" : "Return to Book Workspace"}
          </button>
        ) : null}
        {!useUnifiedBookSetupV1E5 && !(v1e4dEnabled && v1e4dBookActive) ? <ChildrensBookWorkspaceNavigator
          lang={lang}
          currentWorkspace={
            currentWorkspace
          }
          projectTitle={
            draft.identity?.title
          }
          projectPlan={draft}
          childrensBookAssets={
            childrensBookAssets
          }
          onNavigate={openWorkspace}
        /> : null}

        {content}
      </>
    );
  }

  function updateSection(
    section,
    field,
    value
  ) {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }));
  }

  function textPresent(value) {
    return String(value ?? "").trim().length > 0;
  }

  function decisionMade(value) {
    const normalized =
      String(value ?? "").trim();

    return (
      textPresent(normalized) &&
      normalized !== "not_decided"
    );
  }

  function trimSizeComplete(
    trimSize,
    customTrimSize
  ) {
    const normalized =
      String(trimSize ?? "").trim();

    if (!decisionMade(normalized)) {
      return false;
    }

    if (
      normalized ===
      "custom_dimensions"
    ) {
      return textPresent(
        customTrimSize
      );
    }

    return true;
  }

  function currentStepIsComplete() {
    if (step === 0) {
      return (
        textPresent(draft.identity.title) &&
        textPresent(draft.identity.language) &&
        textPresent(draft.identity.authorName)
      );
    }

    if (step === 1) {
      return (
        textPresent(draft.audience.ageRange) &&
        textPresent(
          draft.audience.readingArrangement
        ) &&
        decisionMade(
          draft.audience.readingLevel
        )
      );
    }

    if (step === 2) {
      return (
        textPresent(draft.bookForm.type) &&
        textPresent(draft.bookForm.workingUnit) &&
        (
          draft.bookForm.type !== "custom" ||
          textPresent(draft.bookForm.customType)
        )
      );
    }

    if (step === 3) {
      return (
        textPresent(
          draft.visualFormat.orientation
        ) &&
        trimSizeComplete(
          draft.visualFormat.trimSize,
          draft.visualFormat.customTrimSize
        ) &&
        textPresent(
          draft.visualFormat.publicationFormat
        )
      );
    }

    return textPresent(
      draft.collaboration.mode
    );
  }

  function saveCurrentStep() {
    if (!currentStepIsComplete()) {
      setStepMessage(
        isFR
          ? "Complétez tous les champs obligatoires avant d’enregistrer cette étape."
          : "Complete all required fields before saving this step."
      );

      return;
    }

    setSavedSteps((current) =>
      current.map((saved, index) =>
        index === step ? true : saved
      )
    );

    setStepMessage(
      isFR
        ? "Étape enregistrée dans ce brouillon local."
        : "Step saved in this local draft."
    );
  }

  function editCurrentStep() {
    setSavedSteps((current) =>
      current.map((saved, index) =>
        index === step ? false : saved
      )
    );

    setStepMessage(
      isFR
        ? "Vous pouvez maintenant modifier cette étape."
        : "You can now edit this step."
    );
  }

  function moveToStep(index) {
    setStep(index);
    setStepMessage("");
  }

  function displayValue(value) {
    const normalized = String(value ?? "").trim();

    if (!normalized) {
      return isFR
        ? "Non renseigné"
        : "Not provided";
    }

    const labels = {
      en: "English",
      fr: "Français",
      bilingual: isFR
        ? "Bilingue"
        : "Bilingual",

      solo: "Solo",
      collaborative: isFR
        ? "Collaboratif"
        : "Collaborative",
      guided_by_moderator: isFR
        ? "Guidé par un Moderator"
        : "Guided by Moderator",

      picture_book: isFR
        ? "Album illustré"
        : "Picture book",
      illustrated_storybook: isFR
        ? "Histoire illustrée"
        : "Illustrated storybook",
      early_reader: isFR
        ? "Première lecture"
        : "Early reader",
      chapter_book: isFR
        ? "Livre à chapitres"
        : "Chapter book",
      educational_story: isFR
        ? "Histoire éducative"
        : "Educational story",
      activity_book: isFR
        ? "Livre d’activités"
        : "Activity book",
      poetry_rhyme: isFR
        ? "Poésie ou comptines"
        : "Poetry or rhyme book",
      folktale: isFR
        ? "Conte traditionnel"
        : "Folktale",
      custom: isFR
        ? "Personnalisé"
        : "Custom",

      single_pages: isFR
        ? "Pages simples"
        : "Single pages",
      double_page_spreads: isFR
        ? "Doubles pages"
        : "Double-page spreads",
      scenes: isFR
        ? "Scènes"
        : "Scenes",
      short_chapters: isFR
        ? "Chapitres courts"
        : "Short chapters",

      read_aloud: isFR
        ? "Lecture accompagnée"
        : "Read aloud",
      independent: isFR
        ? "Lecture autonome"
        : "Independent reading",
      shared: isFR
        ? "Lecture partagée"
        : "Shared reading",

      read_aloud_pre_reader: isFR
        ? "Lecture à voix haute / Pré-lecteur"
        : "Read-aloud / Pre-reader",
      beginning_reader: isFR
        ? "Lecteur débutant"
        : "Beginning reader",
      early_independent_reader: isFR
        ? "Lecteur autonome débutant"
        : "Early independent reader",
      independent_reader: isFR
        ? "Lecteur autonome"
        : "Independent reader",
      fluent_reader: isFR
        ? "Lecteur confirmé"
        : "Fluent reader",
      advanced_young_reader: isFR
        ? "Jeune lecteur avancé"
        : "Advanced young reader",
      mixed_flexible: isFR
        ? "Niveau mixte ou flexible"
        : "Mixed or flexible level",
      not_decided: isFR
        ? "Pas encore décidé"
        : "Not yet decided",

      "8_x_8_in": "8 × 8 inches",
      "8.25_x_8.25_in":
        "8.25 × 8.25 inches",
      "8.5_x_8.5_in":
        "8.5 × 8.5 inches",
      "8_x_10_in": "8 × 10 inches",
      "8.5_x_11_in":
        "8.5 × 11 inches",
      "5.5_x_8.5_in":
        "5.5 × 8.5 inches",
      "6_x_9_in": "6 × 9 inches",
      a4: "A4",
      a5: "A5",
      custom_dimensions: isFR
        ? "Dimensions personnalisées"
        : "Custom dimensions",

      portrait: "Portrait",
      landscape: isFR
        ? "Paysage"
        : "Landscape",
      square: isFR
        ? "Carré"
        : "Square",

      yes: isFR
        ? "Oui"
        : "Yes",
      no: isFR
        ? "Non"
        : "No",

      separate_from_illustration: isFR
        ? "Séparé de l’illustration"
        : "Separate from illustration",
      over_illustration: isFR
        ? "Sur l’illustration"
        : "Over illustration",
      mixed: isFR
        ? "Disposition mixte"
        : "Mixed placement",

      print: isFR
        ? "Imprimé"
        : "Print",
      digital: isFR
        ? "Numérique"
        : "Digital",
      print_and_digital: isFR
        ? "Imprimé et numérique"
        : "Print and digital",
    };

    return labels[normalized] || normalized;
  }

  function displayTrimSize() {
    if (
      draft.visualFormat.trimSize ===
      "custom_dimensions"
    ) {
      const customSize =
        String(
          draft.visualFormat
            .customTrimSize || ""
        ).trim();

      const customLabel = isFR
        ? "Dimensions personnalisées"
        : "Custom dimensions";

      return customSize
        ? `${customLabel} — ${customSize}`
        : customLabel;
    }

    return displayValue(
      draft.visualFormat.trimSize
    );
  }

  function editStep(index) {
    setReviewing(false);
    setSavedSteps((current) =>
      current.map((saved, itemIndex) =>
        itemIndex === index ? false : saved
      )
    );
    moveToStep(index);
  }

  function toggleOptionalRole(role) {
    setDraft((current) => {
      const existing =
        current.collaboration.optionalRoles;

      const nextRoles =
        existing.includes(role)
          ? existing.filter(
              (item) => item !== role
            )
          : [...existing, role];

      return {
        ...current,
        collaboration: {
          ...current.collaboration,
          optionalRoles: nextRoles,
        },
      };
    });
  }

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(230px, 1fr))",
    gap: 16,
  };

  function renderStep() {
    if (step === 0) {
      return (
        <div style={fieldGrid}>
          <Field
            label={isFR ? "Titre" : "Title"}
            required
            isFR={isFR}
            value={draft.identity.title}
            onChange={(value) =>
              updateSection(
                "identity",
                "title",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Sous-titre"
                : "Subtitle"
            }
            value={draft.identity.subtitle}
            onChange={(value) =>
              updateSection(
                "identity",
                "subtitle",
                value
              )
            }
          />

          <SelectField
            label={
              isFR
                ? "Langue principale"
                : "Primary language"
            }
            required
            isFR={isFR}
            value={draft.identity.language}
            onChange={(value) =>
              updateSection(
                "identity",
                "language",
                value
              )
            }
            options={[
              {
                value: "en",
                label: "English",
              },
              {
                value: "fr",
                label: "Français",
              },
              {
                value: "bilingual",
                label: isFR
                  ? "Bilingue"
                  : "Bilingual",
              },
            ]}
          />

          <Field
            label={
              isFR
                ? "Nom de l’auteur"
                : "Author name"
            }
            required
            isFR={isFR}
            value={
              draft.identity.authorName
            }
            onChange={(value) =>
              updateSection(
                "identity",
                "authorName",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Nom de plume"
                : "Pen name"
            }
            value={draft.identity.penName}
            onChange={(value) =>
              updateSection(
                "identity",
                "penName",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Nom de la série"
                : "Series title"
            }
            value={
              draft.identity.seriesTitle
            }
            onChange={(value) =>
              updateSection(
                "identity",
                "seriesTitle",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Numéro dans la série"
                : "Series number"
            }
            value={
              draft.identity.seriesNumber
            }
            onChange={(value) =>
              updateSection(
                "identity",
                "seriesNumber",
                value
              )
            }
          />
        </div>
      );
    }

    if (step === 1) {
      return (
        <div style={fieldGrid}>
          <Field
            label={
              isFR
                ? "Tranche d’âge"
                : "Age range"
            }
            required
            isFR={isFR}
            guidance={
              isFR
                ? "Âge prévu du jeune lecteur, par exemple 4–7 ou 8–10 ans."
                : "The intended age of the young reader, for example 4–7 or 8–10."
            }
            value={draft.audience.ageRange}
            placeholder="4–7, 8–10..."
            onChange={(value) =>
              updateSection(
                "audience",
                "ageRange",
                value
              )
            }
          />

          <SelectField
            label={
              isFR
                ? "Organisation de la lecture"
                : "Reading arrangement"
            }
            required
            isFR={isFR}
            guidance={
              isFR
                ? "Indiquez comment le livre sera généralement lu : par un adulte, seul ou à plusieurs."
                : "Choose how the book will usually be read: by an adult, independently, or together."
            }
            value={
              draft.audience
                .readingArrangement
            }
            onChange={(value) =>
              updateSection(
                "audience",
                "readingArrangement",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir"
                  : "Choose",
              },
              {
                value: "read_aloud",
                label: isFR
                  ? "Lecture accompagnée"
                  : "Read aloud",
              },
              {
                value: "independent",
                label: isFR
                  ? "Lecture autonome"
                  : "Independent reading",
              },
              {
                value: "shared",
                label: isFR
                  ? "Lecture partagée"
                  : "Shared reading",
              },
            ]}
          />

          <SelectField
            label={
              isFR
                ? "Niveau de lecture"
                : "Reading level"
            }
            required
            isFR={isFR}
            guidance={
              isFR
                ? "Décrit la difficulté du texte et le degré d’autonomie du jeune lecteur."
                : "Describes the language difficulty and the young reader’s level of independence."
            }
            value={
              draft.audience.readingLevel
            }
            onChange={(value) =>
              updateSection(
                "audience",
                "readingLevel",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir un niveau"
                  : "Choose a level",
              },
              {
                value: "read_aloud_pre_reader",
                label: isFR
                  ? "Lecture à voix haute / Pré-lecteur"
                  : "Read-aloud / Pre-reader",
              },
              {
                value: "beginning_reader",
                label: isFR
                  ? "Lecteur débutant"
                  : "Beginning reader",
              },
              {
                value: "early_independent_reader",
                label: isFR
                  ? "Lecteur autonome débutant"
                  : "Early independent reader",
              },
              {
                value: "independent_reader",
                label: isFR
                  ? "Lecteur autonome"
                  : "Independent reader",
              },
              {
                value: "fluent_reader",
                label: isFR
                  ? "Lecteur confirmé"
                  : "Fluent reader",
              },
              {
                value: "advanced_young_reader",
                label: isFR
                  ? "Jeune lecteur avancé"
                  : "Advanced young reader",
              },
              {
                value: "mixed_flexible",
                label: isFR
                  ? "Niveau mixte ou flexible"
                  : "Mixed or flexible level",
              },
              {
                value: "not_decided",
                label: isFR
                  ? "Pas encore décidé"
                  : "Not yet decided",
              },
            ]}
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Objectif éducatif ou émotionnel"
                : "Educational or emotional objective"
            }
            value={draft.audience.objective}
            onChange={(value) =>
              updateSection(
                "audience",
                "objective",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={isFR ? "Thèmes" : "Themes"}
            value={draft.audience.themes}
            placeholder={
              isFR
                ? "Gentillesse, courage..."
                : "Kindness, courage..."
            }
            onChange={(value) =>
              updateSection(
                "audience",
                "themes",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Cadre culturel"
                : "Cultural setting"
            }
            value={
              draft.audience.culturalSetting
            }
            onChange={(value) =>
              updateSection(
                "audience",
                "culturalSetting",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Niveau scolaire"
                : "School grade"
            }
            value={
              draft.audience.schoolGrade
            }
            onChange={(value) =>
              updateSection(
                "audience",
                "schoolGrade",
                value
              )
            }
          />
        </div>
      );
    }

    if (step === 2) {
      return (
        <div style={fieldGrid}>
          <SelectField
            label={
              isFR
                ? "Forme du livre"
                : "Book form"
            }
            required
            isFR={isFR}
            value={draft.bookForm.type}
            onChange={(value) =>
              updateSection(
                "bookForm",
                "type",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir"
                  : "Choose",
              },
              {
                value: "picture_book",
                label: isFR
                  ? "Album illustré"
                  : "Picture book",
              },
              {
                value: "illustrated_storybook",
                label: isFR
                  ? "Histoire illustrée"
                  : "Illustrated storybook",
              },
              {
                value: "early_reader",
                label: isFR
                  ? "Première lecture"
                  : "Early reader",
              },
              {
                value: "chapter_book",
                label: isFR
                  ? "Livre à chapitres"
                  : "Chapter book",
              },
              {
                value: "educational_story",
                label: isFR
                  ? "Histoire éducative"
                  : "Educational story",
              },
              {
                value: "activity_book",
                label: isFR
                  ? "Livre d’activités"
                  : "Activity book",
              },
              {
                value: "poetry_rhyme",
                label: isFR
                  ? "Poésie ou comptines"
                  : "Poetry or rhyme book",
              },
              {
                value: "folktale",
                label: isFR
                  ? "Conte traditionnel"
                  : "Folktale",
              },
              {
                value: "custom",
                label: isFR
                  ? "Personnalisé"
                  : "Custom",
              },
            ]}
          />

          <SelectField
            label={
              isFR
                ? "Unité de travail"
                : "Working unit"
            }
            required
            isFR={isFR}
            value={
              draft.bookForm.workingUnit
            }
            onChange={(value) =>
              updateSection(
                "bookForm",
                "workingUnit",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir"
                  : "Choose",
              },
              {
                value: "single_pages",
                label: isFR
                  ? "Pages simples"
                  : "Single pages",
              },
              {
                value: "double_page_spreads",
                label: isFR
                  ? "Doubles pages"
                  : "Double-page spreads",
              },
              {
                value: "scenes",
                label: isFR
                  ? "Scènes"
                  : "Scenes",
              },
              {
                value: "short_chapters",
                label: isFR
                  ? "Chapitres courts"
                  : "Short chapters",
              },
            ]}
          />

          {draft.bookForm.type ===
          "custom" ? (
            <Field
              isFR={isFR}
              label={
                isFR
                  ? "Forme personnalisée"
                  : "Custom book form"
              }
              value={
                draft.bookForm.customType
              }
              onChange={(value) =>
                updateSection(
                  "bookForm",
                  "customType",
                  value
                )
              }
            />
          ) : null}
        </div>
      );
    }

    if (step === 3) {
      return (
        <div style={fieldGrid}>
          <Field
            isFR={isFR}
            label={
              isFR
                ? "Fréquence des illustrations"
                : "Illustration frequency"
            }
            value={
              draft.visualFormat
                .illustrationFrequency
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "illustrationFrequency",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Style visuel"
                : "Visual style"
            }
            value={
              draft.visualFormat.visualStyle
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "visualStyle",
                value
              )
            }
          />

          <Field
            isFR={isFR}
            label={
              isFR
                ? "Atmosphère des couleurs"
                : "Colour atmosphere"
            }
            value={
              draft.visualFormat
                .colourAtmosphere
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "colourAtmosphere",
                value
              )
            }
          />

          <SelectField
            label={
              isFR
                ? "Orientation"
                : "Orientation"
            }
            required
            isFR={isFR}
            value={
              draft.visualFormat.orientation
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "orientation",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir"
                  : "Choose",
              },
              {
                value: "portrait",
                label: isFR
                  ? "Portrait"
                  : "Portrait",
              },
              {
                value: "landscape",
                label: isFR
                  ? "Paysage"
                  : "Landscape",
              },
              {
                value: "square",
                label: isFR
                  ? "Carré"
                  : "Square",
              },
            ]}
          />

          <SelectField
            label={
              isFR
                ? "Format ou dimensions"
                : "Trim size or dimensions"
            }
            required
            isFR={isFR}
            guidance={
              isFR
                ? "Choisissez une dimension courante ou sélectionnez des dimensions personnalisées."
                : "Choose a common size or select custom dimensions."
            }
            value={
              draft.visualFormat.trimSize
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "trimSize",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir un format"
                  : "Choose a size",
              },
              {
                value: "8_x_8_in",
                label: "8 × 8 inches",
              },
              {
                value: "8.25_x_8.25_in",
                label: "8.25 × 8.25 inches",
              },
              {
                value: "8.5_x_8.5_in",
                label: "8.5 × 8.5 inches",
              },
              {
                value: "8_x_10_in",
                label: "8 × 10 inches",
              },
              {
                value: "8.5_x_11_in",
                label: "8.5 × 11 inches",
              },
              {
                value: "5.5_x_8.5_in",
                label: "5.5 × 8.5 inches",
              },
              {
                value: "6_x_9_in",
                label: "6 × 9 inches",
              },
              {
                value: "a4",
                label: "A4",
              },
              {
                value: "a5",
                label: "A5",
              },
              {
                value: "custom_dimensions",
                label: isFR
                  ? "Dimensions personnalisées"
                  : "Custom dimensions",
              },
              {
                value: "not_decided",
                label: isFR
                  ? "Pas encore décidé"
                  : "Not yet decided",
              },
            ]}
          />

          {draft.visualFormat.trimSize ===
          "custom_dimensions" ? (
            <Field
              label={
                isFR
                  ? "Dimensions personnalisées"
                  : "Custom dimensions"
              }
              required
              isFR={isFR}
              guidance={
                isFR
                  ? "Indiquez la largeur, la hauteur et l’unité, par exemple 210 × 210 mm."
                  : "Enter width, height, and unit, for example 210 × 210 mm."
              }
              value={
                draft.visualFormat.customTrimSize
              }
              placeholder={
                isFR
                  ? "Exemple : 210 × 210 mm"
                  : "Example: 210 × 210 mm"
              }
              onChange={(value) =>
                updateSection(
                  "visualFormat",
                  "customTrimSize",
                  value
                )
              }
            />
          ) : null}

          <SelectField
            isFR={isFR}
            label={
              isFR
                ? "Fond perdu"
                : "Bleed"
            }
            value={
              draft.visualFormat.bleed
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "bleed",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir"
                  : "Choose",
              },
              {
                value: "yes",
                label: isFR
                  ? "Oui"
                  : "Yes",
              },
              {
                value: "no",
                label: isFR
                  ? "Non"
                  : "No",
              },
            ]}
          />

          <SelectField
            isFR={isFR}
            label={
              isFR
                ? "Placement du texte"
                : "Text placement"
            }
            value={
              draft.visualFormat.textPlacement
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "textPlacement",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir"
                  : "Choose",
              },
              {
                value: "separate_from_illustration",
                label: isFR
                  ? "Séparé de l’illustration"
                  : "Separate from illustration",
              },
              {
                value: "over_illustration",
                label: isFR
                  ? "Sur l’illustration"
                  : "Over illustration",
              },
              {
                value: "mixed",
                label: isFR
                  ? "Disposition mixte"
                  : "Mixed placement",
              },
            ]}
          />

          <SelectField
            label={
              isFR
                ? "Format de publication"
                : "Publication format"
            }
            required
            isFR={isFR}
            value={
              draft.visualFormat
                .publicationFormat
            }
            onChange={(value) =>
              updateSection(
                "visualFormat",
                "publicationFormat",
                value
              )
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Choisir"
                  : "Choose",
              },
              {
                value: "print",
                label: isFR
                  ? "Imprimé"
                  : "Print",
              },
              {
                value: "digital",
                label: isFR
                  ? "Numérique"
                  : "Digital",
              },
              {
                value: "print_and_digital",
                label: isFR
                  ? "Imprimé et numérique"
                  : "Print and digital",
              },
            ]}
          />
        </div>
      );
    }

    const optionalRoles = [
      {
        value: "Illustrator",
        label: isFR
          ? "Illustrateur"
          : "Illustrator",
      },
      {
        value: "Translator",
        label: isFR
          ? "Traducteur"
          : "Translator",
      },
      {
        value: "Editor",
        label: isFR
          ? "Éditeur"
          : "Editor",
      },
      {
        value: "Educational Adviser",
        label: isFR
          ? "Conseiller pédagogique"
          : "Educational Adviser",
      },
      {
        value: "Moderator",
        label: "Moderator",
      },
    ];

    return (
      <div style={{ display: "grid", gap: 22 }}>
        <SelectField
          label={
            isFR
              ? "Mode d’écriture"
              : "Writing mode"
          }
          required
          isFR={isFR}
          guidance={
            isFR
              ? "Choisissez comment vous souhaitez écrire ce livre : seul(e) ou avec d’autres."
              : "Choose how you want to write this book: on your own or with others."
          }
          value={draft.collaboration.mode}
          onChange={(value) =>
            updateSection(
              "collaboration",
              "mode",
              value
            )
          }
          options={[
            {
              value: "",
              label: isFR
                ? "Choisir un mode d’écriture"
                : "Choose a writing mode",
            },
            {
              value: "solo",
              label: "Solo",
            },
            {
              value: "collaborative",
              label: isFR
                ? "Collaboratif"
                : "Collaborative",
            },
            {
              value: "guided_by_moderator",
              label: isFR
                ? "Guidé par un Moderator"
                : "Guided by Moderator",
            },
          ]}
        />

        <fieldset
          style={{
            margin: 0,
            padding: 16,
            borderRadius: 14,
            border: "1px solid #cbd5e1",
          }}
        >
          <legend
            style={{
              padding: "0 7px",
              fontWeight: 900,
            }}
          >
            {isFR
              ? "Participants facultatifs"
              : "Optional participants"}
          </legend>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(190px, 1fr))",
              gap: 10,
            }}
          >
            {optionalRoles.map((role) => (
              <label
                key={role.value}
                style={{
                  display: "flex",
                  gap: 9,
                  alignItems: "center",
                  fontWeight: 700,
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    draft.collaboration
                      .optionalRoles
                      .includes(role.value)
                  }
                  onChange={() =>
                    toggleOptionalRole(
                      role.value
                    )
                  }
                />

                <span>{role.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div
          className="ns-card"
          style={{
            padding: 16,
            borderRadius: 14,
            background: "#fff7ed",
            border:
              "1px solid rgba(234,88,12,0.25)",
          }}
        >
          <strong>
            {persistenceEnabled
              ? (
                  isFR
                    ? "Enregistrement du projet"
                    : "Project saving"
                )
              : (
                  isFR
                    ? "Brouillon local uniquement"
                    : "Local draft only"
                )}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin: "7px 0 0",
              lineHeight: 1.55,
            }}
          >
            {persistenceEnabled
              ? (
                  isFR
                    ? "Utilisez « Enregistrer le Blueprint » pour conserver vos modifications avant de quitter ou d’actualiser cette page."
                    : "Use “Save Blueprint” to preserve your changes before leaving or refreshing this page."
                )
              : (
                  isFR
                    ? "Aucune donnée n’est envoyée au serveur. Le brouillon disparaîtra si vous actualisez ou quittez cette page."
                    : "No data is sent to the server. This draft will disappear if you refresh or leave the page."
                )}
          </p>
        </div>
      </div>
    );
  }

  if (v1e4dEnabled && v1e4dBookActive && !wholeBookReviewContext) {
    return withPersistenceControls(
      <ChildrensBookNovelIntegratedWorkspaceV1E4D
        lang={lang} draft={draft} readOnly={readOnly}
        savedAudienceReady={savedAudienceReady}
        childrensBookAssets={childrensBookAssets}
        onLoadChildrensBookAssetFile={onLoadChildrensBookAssetFile}
        hasUnsavedChanges={persistenceSnapshot(draft) !== lastSavedSnapshot}
        onChangeManuscript={(field,value)=>updateSection("manuscriptPlan",field,value)}
        onPositionChange={setManuscriptPlanPosition}
        onOpenExistingWorkspace={openWorkspace}
        onExit={onExit}
      />
    );
  }

  if (planningBookMap) {
    return withPersistenceControls(
      <ChildrensBookPageSpreadPlanning
        lang={lang}
        bookMap={draft.bookMap}
        manuscriptPlan={
          draft.manuscriptPlan
        }
        projectPlan={draft}
        childrensBookAssets={
          childrensBookAssets
        }
        assetManagementEnabled={
          Boolean(currentProjectId)
        }
        childrensBookAssetsLoading={
          childrensBookAssetsLoading
        }
        childrensBookAssetsError={
          childrensBookAssetsError
        }
        childrensBookAssetActionId={
          childrensBookAssetActionId
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onUploadChildrensBookIllustration={
          onUploadChildrensBookIllustration
        }
        onRemoveChildrensBookAsset={
          onRemoveChildrensBookAsset
        }
        persistenceEnabled={
          persistenceEnabled
        }
        initialUnitId={
          wholeBookReviewContext?.unitId ||
          ""
        }
        returnToWholeBookReview={
          Boolean(
            wholeBookReviewContext
          )
        }
        onChange={(field, value) =>
          updateSection(
            "bookMap",
            field,
            value
          )
        }
        onManuscriptChange={(field, value) =>
          updateSection(
            "manuscriptPlan",
            field,
            value
          )
        }
        onApplyStructuralManuscriptTransition={
            persistenceEnabled
              ? applyBookStructureStructuralManuscriptTransition
              : undefined
          }
        structuralManuscriptTransitionSaving={
          persisting
        }
        onReturn={
          wholeBookReviewContext
            ? returnToWholeBookReview
            : () =>
                openWorkspace(
                  "blueprint"
                )
        }
        onExit={onExit}
      />
    );
  }

  if (planningManuscript && readOnly && !persistenceEnabled &&
      process.env.REACT_APP_CHILDRENS_BOOK_REAL_NOVEL_PARITY_V1E_ENABLED === "true") {
    // Read-only staging review only. No real book writing/approval routes or data mutation.
    return <ChildrensBookNovelParityV1E
      title={draft.identity?.title || ""}
      units={draft.bookMap?.units || []}
      entries={draft.manuscriptPlan?.entries || []}
      lang={lang}
      onReturn={() => openWorkspace("blueprint")}
    />;
  }

  if (planningManuscript) {
    return withPersistenceControls(
      <ChildrensBookManuscriptPlan
        lang={lang}
        readOnly={readOnly || !savedAudienceReady}
        novelWritingStyle={process.env.REACT_APP_CHILDRENS_BOOK_NOVEL_WRITER_V1_ENABLED === "true"}
        chapterSceneNavigationV1={process.env.REACT_APP_CHILDRENS_BOOK_CHAPTER_SCENE_NAV_V1_ENABLED === "true"}
        chapterSceneManagementV1B={process.env.REACT_APP_CHILDRENS_BOOK_CHAPTER_SCENE_MANAGEMENT_V1B_ENABLED === "true"}
        projectPlan={draft}
        bookMap={draft.bookMap}
        manuscriptPlan={draft.manuscriptPlan}
        childrensBookAssets={
          childrensBookAssets
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onPreviewManuscriptImport={
          readOnly || !savedAudienceReady ? undefined : onPreviewManuscriptImport
        }
        onApplyManuscriptImport={
          readOnly || !savedAudienceReady ? undefined : applyReviewedManuscriptImport
        }
        onApplyManuscriptUnitDivision={
          savedAudienceReady && persistenceEnabled &&
          typeof onSaveProject ===
            "function"
            ? applyReviewedManuscriptUnitDivision
            : undefined
        }
        manuscriptUnitDivisionSaving={
          persisting
        }
        initialUnitId={
          wholeBookReviewContext?.unitId ||
          manuscriptPlanPosition.unitId ||
          ""
        }
        initialRangeStart={
          wholeBookReviewContext
            ? wholeBookReviewContext
                .windowStart
            : manuscriptPlanPosition
                .rangeStart
        }
        returnToWholeBookReview={
          Boolean(
            wholeBookReviewContext
          )
        }
        onPositionChange={
          wholeBookReviewContext
            ? undefined
            : setManuscriptPlanPosition
        }
        onChange={(field, value) =>
          updateSection("manuscriptPlan", field, value)
        }
        onOpenBookMap={
          openBookMapFromCorrectionWorkspace
        }
        onReturn={
          wholeBookReviewContext
            ? returnToWholeBookReview
            : () =>
                openWorkspace(
                  "blueprint"
                )
        }
        onExit={onExit}
      />
    );
  }

  if (planningIllustrations) {
    return withPersistenceControls(
      <ChildrensBookIllustrationPlan
        lang={lang}
        bookMap={draft.bookMap}
        illustrationPlan={draft.illustrationPlan}
        initialUnitId={
          wholeBookReviewContext?.unitId ||
          ""
        }
        returnToWholeBookReview={
          Boolean(
            wholeBookReviewContext
          )
        }
        assetManagementEnabled={
          Boolean(currentProjectId)
        }
        childrensBookAssets={
          childrensBookAssets
        }
        childrensBookAssetsLoading={
          childrensBookAssetsLoading
        }
        childrensBookAssetsError={
          childrensBookAssetsError
        }
        childrensBookAssetActionId={
          childrensBookAssetActionId
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onUploadChildrensBookIllustration={
          onUploadChildrensBookIllustration
        }
        onRegisterChildrensBookAsset={
          onRegisterChildrensBookAsset
        }
        onReplaceChildrensBookAsset={
          onReplaceChildrensBookAsset
        }
        onRemoveChildrensBookAsset={
          onRemoveChildrensBookAsset
        }
        onChange={(field, value) =>
          updateSection("illustrationPlan", field, value)
        }
        onOpenBookMap={
          openBookMapFromCorrectionWorkspace
        }
        onReturn={
          wholeBookReviewContext
            ? returnToWholeBookReview
            : () =>
                openWorkspace(
                  "blueprint"
                )
        }
        onExit={onExit}
      />
    );
  }

  if (viewingBookAssembly) {
    return withPersistenceControls(
      <ChildrensBookBookAssembly
        lang={lang}
        projectPlan={draft}
        persistenceEnabled={
          persistenceEnabled
        }
        projectId={
          currentProjectId
        }
        initialUnitId={
          wholeBookReviewContext?.unitId ||
          ""
        }
        initialWindowStart={
          wholeBookReviewContext
            ?.windowStart || 0
        }
        onLoadChildrensBookAssemblyRange={
          onLoadChildrensBookAssemblyRange
        }
        onLoadChildrensBookAssemblyUnit={
          onLoadChildrensBookAssemblyUnit
        }
        childrensBookAssets={
          childrensBookAssets
        }
        childrensBookAssetsLoading={
          childrensBookAssetsLoading
        }
        childrensBookAssetsError={
          childrensBookAssetsError
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onOpenBookMap={(context) =>
          openWholeBookCorrection(
            "book-map",
            context
          )
        }
        onOpenManuscriptPlan={(context) =>
          openWholeBookCorrection(
            "manuscript-plan",
            context
          )
        }
        onOpenIllustrationPlan={(context) =>
          openWholeBookCorrection(
            "illustration-plan",
            context
          )
        }
        onOpenCoverPlan={(context) =>
          openWholeBookCorrection(
            "cover-plan",
            context
          )
        }
        onReturn={() =>
          openWorkspace("blueprint")
        }
        onExit={onExit}
      />
    );
  }

  if (viewingBookPreview) {
    return withPersistenceControls(
      <ChildrensBookBookPreview
        lang={lang}
        projectPlan={draft}
        childrensBookAssets={
          childrensBookAssets
        }
        childrensBookAssetsLoading={
          childrensBookAssetsLoading
        }
        childrensBookAssetsError={
          childrensBookAssetsError
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onOpenBookAssembly={() =>
          openWorkspace("book-assembly")
        }
        onOpenManuscriptPlan={() =>
          openWorkspace("manuscript-plan")
        }
        onOpenIllustrationPlan={() =>
          openWorkspace("illustration-plan")
        }
        onOpenCoverPlan={() =>
          openWorkspace("cover-plan")
        }
        onExit={onExit}
      />
    );
  }

  if (reviewingPublicationReadiness) {
    return withPersistenceControls(
      <ChildrensBookPublicationReadiness
        lang={lang}
        projectPlan={draft}
        projectId={
          currentProjectId
        }
        childrensBookAssets={
          childrensBookAssets
        }
        onLoadPublicationCompatibility={
          onLoadChildrensBookPublicationCompatibility
        }
        onLoadPublicationProduction={
          onLoadChildrensBookPublicationProduction
        }
        onLoadPublicationInteriorReview={
          onLoadChildrensBookPublicationInteriorReview
        }
        onLoadPublicationCoverReview={
          onLoadChildrensBookPublicationCoverReview
        }
        onLoadPublicationFinalInterior={
          onLoadChildrensBookPublicationFinalInterior
        }
        onLoadPublicationFinalCover={
          onLoadChildrensBookPublicationFinalCover
        }
        onRegeneratePublicationInterior={
          onRegenerateChildrensBookPublicationInterior
        }
        onApprovePublicationInterior={
          onApproveChildrensBookPublicationInterior
        }
        onRegeneratePublicationCover={
          onRegenerateChildrensBookPublicationCover
        }
        onApprovePublicationCover={
          onApproveChildrensBookPublicationCover
        }
        onStartPublicationProduction={
          onStartChildrensBookPublicationProduction
        }
        onOpenBookAssembly={() =>
          openWorkspace("book-assembly")
        }
        onOpenPublicationProof={() =>
          openWorkspace(
            "publication-proof"
          )
        }
        onOpenProjectPlan={() =>
          openWorkspace("project-plan")
        }
        onOpenBookMap={() =>
          openWorkspace("book-map")
        }
        onOpenCoverPlan={() =>
          openWorkspace("cover-plan")
        }
        onOpenManuscriptPlan={() =>
          openWorkspace("manuscript-plan")
        }
        onOpenIllustrationPlan={() =>
          openWorkspace("illustration-plan")
        }
        onReturn={() =>
          openWorkspace("blueprint")
        }
        onExit={onExit}
      />
    );
  }

  if (viewingPublicationProof) {
    return withPersistenceControls(
      <ChildrensBookPublicationProof
        lang={lang}
        projectPlan={draft}
        projectId={
          currentProjectId
        }
        childrensBookAssets={
          childrensBookAssets
        }
        childrensBookAssetsLoading={
          childrensBookAssetsLoading
        }
        childrensBookAssetsError={
          childrensBookAssetsError
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onLoadCoverPalettePreview={
          onLoadChildrensBookCoverPalettePreview
        }
        onOpenPublicationReadiness={() =>
          openWorkspace(
            "publication-readiness"
          )
        }
        onOpenPublicationHandoff={() =>
          openWorkspace(
            "publication-handoff"
          )
        }
        onOpenCoverPlan={() =>
          openWorkspace("cover-plan")
        }
        onOpenManuscriptPlan={
          openPublicationProofManuscriptCorrection
        }
        onOpenIllustrationPlan={() =>
          openWorkspace(
            "illustration-plan"
          )
        }
        onExit={onExit}
      />
    );
  }

  if (viewingPublicationHandoff) {
    return withPersistenceControls(
      <ChildrensBookPublicationHandoff
        lang={lang}
        projectPlan={draft}
        childrensBookAssets={
          childrensBookAssets
        }
        onPublish={
          publishChildrensBookOnNextScenes
        }
        onOpenPublicationProof={() =>
          openWorkspace(
            "publication-proof"
          )
        }
        onOpenBookAssembly={() =>
          openWorkspace("book-assembly")
        }
        onOpenProjectPlan={() =>
          openWorkspace("project-plan")
        }
        onOpenCoverPlan={() =>
          openWorkspace("cover-plan")
        }
        onOpenManuscriptPlan={() =>
          openWorkspace("manuscript-plan")
        }
        onOpenIllustrationPlan={() =>
          openWorkspace(
            "illustration-plan"
          )
        }
        onOpenPublishedBook={() =>
          openWorkspace(
            "published-book"
          )
        }
        onReturn={() =>
          openWorkspace("blueprint")
        }
        onExit={onExit}
      />
    );
  }


  if (viewingPublishedBook) {
    return withPersistenceControls(
      <ChildrensBookApprovedEditionGateV1
        projectId={currentProjectId}
        lang={lang}
        projectPlan={draft}
        projectVisibility={
          initialDraft?.project
            ?.visibility ||
          "private_draft"
        }
        onUpdateVisibility={
          persistenceEnabled &&
          currentProjectId &&
          typeof onUpdateChildrensBookVisibility ===
            "function"
            ? (visibility) =>
                onUpdateChildrensBookVisibility(
                  currentProjectId,
                  visibility
                )
            : undefined
        }
        childrensBookAssets={
          childrensBookAssets
        }
        childrensBookAssetsLoading={
          childrensBookAssetsLoading
        }
        childrensBookAssetsError={
          childrensBookAssetsError
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onOpenPublicationHandoff={() =>
          openWorkspace(
            "publication-handoff"
          )
        }
        onReturn={() =>
          openWorkspace("blueprint")
        }
        onExit={onExit}
      />
    );
  }

  if (planningCover) {
    return withPersistenceControls(
      <ChildrensBookCoverPlanning
        lang={lang}
        coverPlan={draft.coverPlan}
        projectPlan={draft}
        returnToWholeBookReview={
          Boolean(
            wholeBookReviewContext
          )
        }
        assetManagementEnabled={
          Boolean(currentProjectId)
        }
        childrensBookAssets={
          childrensBookAssets
        }
        childrensBookAssetsLoading={
          childrensBookAssetsLoading
        }
        childrensBookAssetsError={
          childrensBookAssetsError
        }
        childrensBookAssetActionId={
          childrensBookAssetActionId
        }
        onLoadChildrensBookAssetFile={
          onLoadChildrensBookAssetFile
        }
        onUploadChildrensBookCover={
          onUploadChildrensBookCover
        }
        onRegisterChildrensBookAsset={
          onRegisterChildrensBookAsset
        }
        onReplaceChildrensBookAsset={
          onReplaceChildrensBookAsset
        }
        onRemoveChildrensBookAsset={
          onRemoveChildrensBookAsset
        }
        onChange={(field, value) =>
          updateSection(
            "coverPlan",
            field,
            value
          )
        }
        onReturn={
          wholeBookReviewContext
            ? returnToWholeBookReview
            : () =>
                openWorkspace(
                  "blueprint"
                )
        }
        onExit={onExit}
      />
    );
  }

  if (viewingBlueprint) {
    return withPersistenceControls(
      <ChildrensBookBlueprintOverview
        lang={lang}
        projectPlan={draft}
        childrensBookAssets={
          childrensBookAssets
        }
        onOpenProjectPlan={() =>
          openWorkspace("project-plan")
        }
        onOpenBookMap={() =>
          openWorkspace("book-map")
        }
        onOpenCoverPlan={() =>
          openWorkspace("cover-plan")
        }
        onOpenManuscriptPlan={() =>
          openWorkspace("manuscript-plan")
        }
        onOpenIllustrationPlan={() =>
          openWorkspace("illustration-plan")
        }
        onOpenBookAssembly={() =>
          openWorkspace("book-assembly")
        }
        onOpenPublicationReadiness={() =>
          openWorkspace(
            "publication-readiness"
          )
        }
        onOpenPublicationHandoff={() =>
          openWorkspace(
            "publication-handoff"
          )
        }
        onPublish={
          publishChildrensBookOnNextScenes
        }
        onOpenPublishedBook={() =>
          openWorkspace(
            "published-book"
          )
        }
        onReturn={() =>
          openWorkspace("project-plan")
        }
        onExit={onExit}
      />
    );
  }

  if (reviewing) {
    const reviewSections = [
      {
        title: isFR
          ? "Identité du projet"
          : "Project Identity",
        stepIndex: 0,
        rows: [
          [isFR ? "Titre" : "Title", draft.identity.title],
          [isFR ? "Sous-titre" : "Subtitle", draft.identity.subtitle],
          [
            isFR ? "Langue principale" : "Primary language",
            draft.identity.language,
          ],
          [
            isFR ? "Nom de l’auteur" : "Author name",
            draft.identity.authorName,
          ],
          [
            isFR ? "Nom de plume" : "Pen name",
            draft.identity.penName,
          ],
          [
            isFR ? "Nom de la série" : "Series title",
            draft.identity.seriesTitle,
          ],
          [
            isFR ? "Numéro dans la série" : "Series number",
            draft.identity.seriesNumber,
          ],
        ],
      },
      {
        title: isFR
          ? "Public visé"
          : "Intended Audience",
        stepIndex: 1,
        rows: [
          [
            isFR ? "Tranche d’âge" : "Age range",
            draft.audience.ageRange,
          ],
          [
            isFR
              ? "Organisation de la lecture"
              : "Reading arrangement",
            draft.audience.readingArrangement,
          ],
          [
            isFR ? "Niveau de lecture" : "Reading level",
            draft.audience.readingLevel,
          ],
          [
            isFR
              ? "Objectif éducatif ou émotionnel"
              : "Educational or emotional objective",
            draft.audience.objective,
          ],
          [isFR ? "Thèmes" : "Themes", draft.audience.themes],
          [
            isFR ? "Cadre culturel" : "Cultural setting",
            draft.audience.culturalSetting,
          ],
          [
            isFR ? "Niveau scolaire" : "School grade",
            draft.audience.schoolGrade,
          ],
        ],
      },
      {
        title: isFR
          ? "Forme du livre"
          : "Book Form",
        stepIndex: 2,
        rows: [
          [
            isFR ? "Type de livre" : "Book form",
            draft.bookForm.type,
          ],
          [
            isFR ? "Forme personnalisée" : "Custom book form",
            draft.bookForm.customType,
          ],
          [
            isFR ? "Unité de travail" : "Working unit",
            draft.bookForm.workingUnit,
          ],
        ],
      },
      {
        title: isFR
          ? "Format visuel"
          : "Visual Format",
        stepIndex: 3,
        rows: [
          [
            isFR
              ? "Fréquence des illustrations"
              : "Illustration frequency",
            draft.visualFormat.illustrationFrequency,
          ],
          [
            isFR ? "Style visuel" : "Visual style",
            draft.visualFormat.visualStyle,
          ],
          [
            isFR
              ? "Atmosphère des couleurs"
              : "Colour atmosphere",
            draft.visualFormat.colourAtmosphere,
          ],
          [
            isFR ? "Orientation" : "Orientation",
            draft.visualFormat.orientation,
          ],
          [
            isFR
              ? "Format ou dimensions"
              : "Trim size or dimensions",
            displayTrimSize(),
          ],
          [
            isFR ? "Fond perdu" : "Bleed",
            draft.visualFormat.bleed,
          ],
          [
            isFR
              ? "Placement du texte"
              : "Text placement",
            draft.visualFormat.textPlacement,
          ],
          [
            isFR
              ? "Format de publication"
              : "Publication format",
            draft.visualFormat.publicationFormat,
          ],
        ],
      },
      {
        title: isFR
          ? "Mode de travail"
          : "How You Will Work",
        stepIndex: 4,
        rows: [
          [
            isFR ? "Mode d’écriture" : "Writing mode",
            draft.collaboration.mode,
          ],
          [
            isFR
              ? "Participants facultatifs"
              : "Optional participants",
            draft.collaboration.optionalRoles.join(", "),
          ],
        ],
      },
    ];

    return withPersistenceControls(
      <section
        aria-label={
          isFR
            ? "Vérification du plan"
            : "Review plan"
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#b45309",
              }}
            >
              {isFR
                ? "Brouillon local"
                : "Local draft"}
            </div>

            <h2
              className="ns-section-title"
              style={{ margin: "5px 0 8px" }}
            >
              {isFR
                ? "Vérifiez votre plan"
                : "Review Your Plan"}
            </h2>

            <p
              className="ns-card-meta"
              style={{ margin: 0 }}
            >
              {isFR
                ? "Examinez les informations ci-dessous et revenez directement à une étape pour effectuer une correction."
                : "Review the information below and return directly to any step to make a correction."}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onExit}
          >
            {isFR
              ? "Retour à Mes projets"
              : "Return to My Projects"}
          </button>
        </div>

        <div
          className="ns-card"
          style={{
            padding: 16,
            marginBottom: 18,
            borderRadius: 16,
            background: "#fff7ed",
            border:
              "1px solid rgba(234,88,12,0.25)",
          }}
        >
          <strong>
            {persistenceEnabled
              ? (
                  isFR
                    ? "Vérification avant enregistrement"
                    : "Review before saving"
                )
              : (
                  isFR
                    ? "Aucune donnée n’a été enregistrée"
                    : "Nothing has been saved"
                )}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin: "7px 0 0",
              lineHeight: 1.55,
            }}
          >
            {persistenceEnabled
              ? (
                  isFR
                    ? "Vérifiez ce résumé, puis utilisez « Enregistrer le Blueprint » pour conserver les dernières modifications."
                    : "Review this summary, then use “Save Blueprint” to preserve the latest changes."
                )
              : (
                  isFR
                    ? "Ce résumé existe uniquement dans la mémoire locale de cette page. Il disparaîtra si vous actualisez ou quittez cette page."
                    : "This summary exists only in this page’s local memory. It will disappear if you refresh or leave this page."
                )}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gap: 16,
          }}
        >
          {reviewSections.map((section) => (
            <article
              key={section.title}
              className="ns-card ns-nbb-major-panel"
              style={{
                padding: 20,
                borderRadius: 18,
                border:
                  "1px solid rgba(234,88,12,0.20)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                <h3
                  className="ns-card-title"
                  style={{ margin: 0 }}
                >
                  {section.title}
                </h3>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() =>
                    editStep(section.stepIndex)
                  }
                >
                  {isFR ? "Modifier" : "Edit"}
                </button>
              </div>

              <dl
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  margin: 0,
                }}
              >
                {section.rows.map(([label, value]) => (
                  <div
                    key={label}
                    className="ns-nbb-inner-panel"
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background: "#f8fafc",
                    }}
                  >
                    <dt
                      style={{
                        fontSize: 12,
                        fontWeight: 900,
                        color: "#64748b",
                        marginBottom: 5,
                      }}
                    >
                      {label}
                    </dt>

                    <dd
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        color: "#1e293b",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {displayValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 18,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              editStep(4)
            }
          >
            {isFR
              ? "Retour à la planification"
              : "Return to planning"}
          </button>

          <button
            className="ns-button ns-button-primary"
            type="button"
            onClick={() =>
              openWorkspace("blueprint")
            }
          >
            {isFR
              ? "Ouvrir le NextScenes Book Blueprint"
              : "Open NextScenes Book Blueprint"}
          </button>
        </div>
      </section>
    );
  }

  if (useUnifiedBookSetupV1E5 && !v1e4dBookActive && currentWorkspace === "project-plan") {
    const setupSections = isFR
      ? ["Identité du livre", "Public et lecture", "Forme du livre", "Format visuel", "Collaboration"]
      : ["Book Identity", "Audience & Reading", "Book Form", "Visual Format", "Collaboration"];
    const unsavedSetup = persistenceSnapshot(draft) !== lastSavedSnapshot;
    // Visual guidance only: Book Setup remains one saved Blueprint, not five independent saves.
    const setupGuidance = isFR
      ? [
          'Titre, langue et auteur — identifiez le livre.',
          'Âge, niveau et mode de lecture — enregistrez-les avant de commencer à écrire.',
          'Type de livre et unité de travail — choisissez votre façon de créer.',
          'Orientation, format et publication — précisez-les pour la mise en pages ultérieure.',
          'Mode de travail — solo ou collaboratif selon le projet.',
        ]
      : [
          'Title, language and author — identify your book.',
          'Age range, reading level and arrangement — save these before writing.',
          'Book type and working unit — choose how to create.',
          'Orientation, trim and publication format — plan these for later layout.',
          'Working mode — solo or collaborative as applicable.',
        ];
    return withPersistenceControls(
      <section className="ns-card" data-cb-v1e5-unified-book-setup="true"
        aria-label={isFR ? "Préparation du livre jeunesse" : "Children's Book Setup"}
        style={{padding:"clamp(18px,3vw,28px)",borderRadius:18}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <div>
            <div className="ns-card-meta" style={{fontWeight:800,color:"#047857"}}>
              {isFR ? "ESPACE LIVRE JEUNESSE" : "CHILDREN'S BOOK WORKSPACE"}
            </div>
            <h2 className="ns-section-title" style={{margin:"6px 0"}}>
              {isFR ? "Préparer le livre" : "Book Setup"}
            </h2>
            <p style={{color:"#475569",margin:"4px 0 0"}}>
              {isFR
                ? "Les cinq domaines se modifient librement ici. Les choix de lecture doivent être enregistrés avant d’écrire."
                : "Edit all five planning areas here. Save the three audience choices before writing; other details can follow."}
            </p>
          </div>
          <button className="ns-button ns-button-outline" type="button"
            onClick={()=>{
              if (unsavedSetup && typeof window !== "undefined" &&
                  !window.confirm(isFR ? "Les modifications ne sont pas enregistrées. Quitter ?" : "You have unsaved changes. Leave Book Setup?")) return;
              onExit?.();
            }}>
            {isFR ? "Retour à Mes projets" : "Back to My Projects"}
          </button>
        </div>
        <div data-cb-v1e6c-setup-journey="true" role="note"
          style={{marginTop:18,padding:"13px 16px",border:"1px solid #cbd5e1",borderRadius:12,background:"#f8fafc"}}>
          <strong style={{color:"#0f172a"}}>{isFR ? "Votre parcours" : "Your writing journey"}</strong>
          <p style={{margin:"5px 0 0",color:"#475569",fontSize:14}}>
            {isFR
              ? "1. Préparer le livre  →  2. Organiser chapitres et scènes  →  3. Écrire dans Writer Studio. Les pages et illustrations viennent ensuite."
              : "1. Book Setup  →  2. Organize Chapters & Scenes  →  3. Write in Writer Studio. Pages and illustrations come later."}
          </p>
        </div>
        <div role="group" aria-label={isFR ? "Domaines de préparation" : "Book Setup sections"}
          style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:8,margin:"18px 0"}}>
          {setupSections.map((label,index)=>(
            <button type="button" key={label}
              data-cb-v1e5-setup-section={index}
              aria-pressed={step===index}
              className="ns-button ns-button-outline"
              onClick={()=>setStep(index)}
              style={{minHeight:56,border:step===index?"2px solid #047857":undefined,
                background:step===index?"#ecfdf5":"#fff",color:"#064e3b",textAlign:"left"}}>
              <span style={{display:"block",fontSize:11,opacity:0.72,marginBottom:3}}>
                {isFR ? "Section" : "Section"} {index+1}/5
              </span>
              <span style={{fontWeight:800}}>{label}</span>
              {index === 1 ? <span style={{display:"block",fontSize:11,marginTop:3}}>
                {isFR ? "Obligatoire avant l’écriture" : "Required before writing"}
              </span> : null}
            </button>
          ))}
        </div>
        <div role="region" aria-label={setupSections[step]}
          style={{padding:"clamp(18px,3vw,28px)",border:"1px solid #cbd5e1",borderRadius:14,background:"#fff"}}>
          <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",flexWrap:"wrap"}}>
            <h3 style={{margin:"0 0 8px",fontSize:19}}>{setupSections[step]}</h3>
            <span data-cb-v1e6c-section-counter="true" style={{fontSize:12,fontWeight:800,color:"#475569"}}>
              {isFR ? "Section" : "Section"} {step+1}/5
            </span>
          </div>
          <p data-cb-v1e6c-section-guidance="true" style={{margin:"0 0 18px",color:"#475569",fontSize:14}}>
            {setupGuidance[step]}
          </p>
          {renderStep()}
          <div data-cb-v1e6c-section-navigation="true"
            style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginTop:24,paddingTop:16,borderTop:"1px solid #e2e8f0"}}>
            <button className="ns-button ns-button-outline" type="button"
              disabled={step===0} onClick={()=>setStep(current=>Math.max(0,current-1))}>
              {isFR ? "← Section précédente" : "← Previous Section"}
            </button>
            <button className="ns-button ns-button-outline" type="button"
              disabled={step===4} onClick={()=>setStep(current=>Math.min(4,current+1))}>
              {isFR ? "Section suivante →" : "Next Section →"}
            </button>
          </div>
        </div>
        <p className="ns-card-meta" data-cb-v1e6c-single-save-guide="true"
          style={{margin:"16px 0",padding:"11px 14px",border:"1px solid #d1d5db",borderRadius:10}}>
          {isFR
            ? "La navigation entre sections n’enregistre rien. Utilisez « Enregistrer » en haut pour conserver toutes les modifications du livre."
            : "Switching sections does not save. Use Save Changes above to save all Book Setup edits together."}
        </p>
        {v1e4dEnabled ? (
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <button className="ns-button ns-button-primary" type="button"
              disabled={persisting || unsavedSetup}
              onClick={()=>openWorkspace("novel-reuse-workspace-v1e4d")}>
              {isFR ? "Retour à l’espace Livre" : "Return to Book Workspace"}
            </button>
            {unsavedSetup ? <span role="status" className="ns-card-meta">
              {isFR ? "Enregistrez vos modifications avant de retourner au livre."
                : "Save your changes before returning to the book."}
            </span> : null}
          </div>
        ) : (
          <p className="ns-card-meta" role="note">
            {isFR ? "Après le premier enregistrement, le nouvel espace Livre s'ouvrira."
              : "After the first save, your new Book Workspace opens."}
          </p>
        )}
      </section>
    );
  }

  return withPersistenceControls(
    <section
      aria-label={
        isFR
          ? "Création d’un livre pour enfants"
          : "Children’s Book creation"
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#b45309",
            }}
          >
            {isFR
              ? "Plan du projet"
              : "Project Plan"}
          </div>

          <h2
            className="ns-section-title"
            style={{ margin: "5px 0 6px" }}
          >
            {steps[step]}
          </h2>

          <p
            className="ns-card-meta"
            style={{ margin: 0 }}
          >
            {isFR
              ? `Étape ${step + 1} sur ${steps.length}`
              : `Step ${step + 1} of ${steps.length}`}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onExit}
        >
          {isFR
            ? "Retour à Mes projets"
            : "Return to My Projects"}
        </button>
      </div>

      <ol
        className="ns-nbb-step-grid"
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(145px, 1fr))",
          gap: 8,
          padding: 0,
          margin: "0 0 20px",
          listStyle: "none",
        }}
      >
        {steps.map((label, index) => {
          const isCurrentStep =
            index === step;

          return (
            <li key={label}>
              <button
                className={
                  isCurrentStep
                    ? "ns-nbb-step-button ns-nbb-step-button-active"
                    : "ns-nbb-step-button"
                }
                type="button"
                onClick={() =>
                  moveToStep(index)
                }
                aria-current={
                  isCurrentStep
                    ? "step"
                    : undefined
                }
                style={{
                  width: "100%",
                  minHeight: 48,
                  padding: "10px 12px",
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 800,
                  textAlign: "left",
                  cursor: "pointer",
                  background:
                    isCurrentStep
                      ? "#ffedd5"
                      : "#f8fafc",
                  border:
                    isCurrentStep
                      ? "1px solid #f97316"
                      : "1px solid #e2e8f0",
                  color:
                    isCurrentStep
                      ? "#9a3412"
                      : "#64748b",
                }}
              >
                {index + 1}. {label}
              </button>
            </li>
          );
        })}
      </ol>

      <div
        className="ns-card ns-nbb-form-panel"
        style={{
          padding: "clamp(18px, 3vw, 26px)",
          borderRadius: 20,
          border:
            "1px solid rgba(234,88,12,0.22)",
          background:
            "linear-gradient(145deg, #ffffff, #fffaf3)",
        }}
      >
        <div
          style={{
            marginBottom: 18,
            paddingBottom: 14,
            borderBottom: "1px solid #e2e8f0",
            fontSize: 13,
            fontWeight: 700,
            color: "#64748b",
          }}
        >
          <span
            style={{
              color: "#dc2626",
              fontWeight: 900,
            }}
          >
            *
          </span>{" "}
          {isFR
            ? "Les champs marqués d’un astérisque sont obligatoires."
            : "Fields marked with an asterisk are required."}
        </div>

        <fieldset
          disabled={savedSteps[step]}
          style={{
            margin: 0,
            padding: 0,
            border: 0,
            minWidth: 0,
            opacity: savedSteps[step]
              ? 0.78
              : 1,
          }}
        >
          {renderStep()}
        </fieldset>

        {stepMessage ? (
          <div
            role="status"
            style={{
              marginTop: 16,
              padding: "11px 13px",
              borderRadius: 10,
              background: savedSteps[step]
                ? "#ecfdf5"
                : "#fff7ed",
              border: savedSteps[step]
                ? "1px solid #86efac"
                : "1px solid #fdba74",
              color: savedSteps[step]
                ? "#166534"
                : "#9a3412",
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            {stepMessage}
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 18,
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={step === 0}
          onClick={() =>
            moveToStep(
              Math.max(0, step - 1)
            )
          }
        >
          {isFR ? "Retour" : "Back"}
        </button>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {savedSteps[step] ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={editCurrentStep}
            >
              {isFR
                ? "Modifier l’étape"
                : "Edit Step"}
            </button>
          ) : (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={saveCurrentStep}
            >
              {isFR
                ? "Enregistrer l’étape"
                : "Save Step"}
            </button>
          )}

          {step < steps.length - 1 ? (
            <button
              className="ns-button ns-button-primary"
              type="button"
              disabled={!savedSteps[step]}
              onClick={() =>
                moveToStep(
                  Math.min(
                    steps.length - 1,
                    step + 1
                  )
                )
              }
            >
              {isFR ? "Continuer" : "Continue"}
            </button>
          ) : (
            <button
              className="ns-button ns-button-primary"
              type="button"
              disabled={!savedSteps[step]}
              onClick={() =>
                setReviewing(true)
              }
            >
              {isFR
                ? "Vérifier le plan local"
                : "Review Local Plan"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
